import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import api, { uploadFile } from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import CourseNameBadge from '../../../components/common/CourseNameBadge';
import LinkedQuizAttempt from '../../../components/training/LinkedQuizAttempt';
import { isStreamableCourseVideo } from '../../../utils/courseVideoPlayback';
import './CourseContent.student.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtSize = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
const typeIcon = (t) => ({ pdf:'bi-file-pdf', video:'bi-camera-video', document:'bi-file-earmark-word', link:'bi-link-45deg', internal_link:'bi-arrow-right-circle', html:'bi-code-slash', notion:'bi-journal-text', quiz:'bi-patch-question' }[t] || 'bi-file');
const typeColor = (t) => ({ pdf:'danger', video:'primary', document:'info', link:'secondary', internal_link:'secondary', html:'success', notion:'dark', quiz:'warning' }[t] || 'secondary');
const statusBadge = (s) => ({ approved:['success','Approved'], pending:['warning','Pending'], rejected:['danger','Rejected'] }[s] || ['secondary',s]);
const THUMBNAIL_EXTENSIONS = new Set(['jpg', 'jpeg', 'jfif', 'png', 'gif', 'webp', 'svg']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'avi', 'mkv', 'mpeg', 'mpg']);
const fileExtension = (file) => String(file?.name || '').split('.').pop().toLowerCase();
const isSupportedThumbnail = (file) => Boolean(file) && (
  ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'].includes(String(file.type || '').toLowerCase())
  || THUMBNAIL_EXTENSIONS.has(fileExtension(file))
);
const readImageDimensions = (file) => new Promise((resolve) => {
  if (!file || typeof Image === 'undefined') return resolve(null);
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    const dimensions = { width: image.naturalWidth || 0, height: image.naturalHeight || 0 };
    URL.revokeObjectURL(objectUrl);
    resolve(dimensions.width && dimensions.height ? dimensions : null);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(null);
  };
  image.src = objectUrl;
});

const uploadLessonThumbnail = async (formData) => {
  // Course-content and on-demand lessons share the same tenant-scoped asset
  // directory and URL format. Use the endpoint available across deployments.
  return uploadFile('/training/on-demand/upload-thumbnail', formData);
};

// Rewrite tenant-scoped /uploads URLs to include an auth token query param,
// since <img> tags can't send Authorization headers and serveAuthenticatedUpload
// requires either the header or ?t=<jwt>. External URLs and data: URIs are
// returned unchanged.
const withAuthToken = (url) => {
  if (!url) return '';
  if (/^(https?:|data:)/.test(url)) return url;
  const token = localStorage.getItem('token');
  if (!token) return url;
  return url.includes('?') ? `${url}&t=${encodeURIComponent(token)}` : `${url}?t=${encodeURIComponent(token)}`;
};

// ─── Modal wrapper ─────────────────────────────────────────────────────────────
const BSModal = ({ show, title, size='', onClose, children, footer }) => {
  if (!show) return null;
  return (
    <div className="modal fade show d-block" style={{ background:'rgba(0,0,0,0.45)', zIndex:1050 }}>
      <div className={`modal-dialog modal-dialog-scrollable${size ? ` modal-${size}` : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose}/>
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
};

// ─── Private HTML Viewer ──────────────────────────────────────────────────────
const PrivateHtmlViewer = ({ filePath }) => {
  const [html, setHtml]       = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');

  useEffect(() => {
    setLoading(true); setErr('');
    api.get('/training/course-content/private-file-content', { params: { filePath } })
      .then(r => setHtml(r.data?.data?.content || ''))
      .catch(e => setErr(e.response?.data?.message || 'Failed to load content'))
      .finally(() => setLoading(false));
  }, [filePath]);

  if (loading) return <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary"/></div>;
  if (err) return <div className="alert alert-danger small py-2">{err}</div>;
  return <div dangerouslySetInnerHTML={{ __html: html }} style={{ lineHeight: 1.7 }} />;
};

// ─── Renders the content of a single item inline ──────────────────────────────
// Convert "shareable" video URLs into their iframe-embeddable equivalents.
// YouTube / Vimeo / Loom serve their watch pages with X-Frame-Options that
// block embedding — only the dedicated /embed paths are iframe-safe. Without
// this rewrite, pasting a youtube.com/watch?v=… link results in a blank
// preview pane and the misleading "Some sites block embedding" message.
const toEmbeddableUrl = (raw) => {
  if (!raw || typeof raw !== 'string') return raw;
  const url = raw.trim();
  // YouTube — handles youtu.be/<id>, youtube.com/watch?v=<id>,
  // youtube.com/embed/<id>, youtube.com/shorts/<id>, with optional ?t= timestamps.
  const yt = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (yt) {
    let q = '';
    const t = url.match(/[?&](?:t|start)=(\d+)/);
    if (t) q = `?start=${t[1]}`;
    return `https://www.youtube.com/embed/${yt[1]}${q}`;
  }
  // Vimeo — vimeo.com/<numeric_id>
  const vm = url.match(/(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  // Loom — loom.com/share/<id> → loom.com/embed/<id>
  const lm = url.match(/loom\.com\/share\/([A-Za-z0-9]+)/);
  if (lm) return `https://www.loom.com/embed/${lm[1]}`;
  // Google Drive viewer link → preview link (allows iframe embedding).
  const gd = url.match(/drive\.google\.com\/file\/d\/([A-Za-z0-9_-]+)/);
  if (gd) return `https://drive.google.com/file/d/${gd[1]}/preview`;
  return url;
};

// Handles all types uniformly:
//   - notion / html: render immediately from the saved structure
//   - link / internal_link: iframe with "Open in new tab" fallback (with
//     YouTube/Vimeo/Loom URLs auto-rewritten to their /embed forms so the
//     preview actually renders instead of showing a blank frame)
//   - video: authenticated byte-range playback without downloading the full file
//   - pdf / document: lazy-fetch blob then render iframe/download
const InlineContentPreview = ({ item, interactiveQuiz = false }) => {
  const streamVideo = isStreamableCourseVideo(item);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [loadError, setLoadError]   = useState('');

  useEffect(() => {
    const needsBlob = ['pdf', 'video', 'document'].includes(item.type);
    if (!needsBlob) return undefined;
    let cancelled = false;
    let objUrl;
    (async () => {
      setLoading(true); setLoadError('');
      try {
        if (streamVideo) {
          const response = await api.get(`/training/course-content/file/${item._id}/playback`);
          const streamPath = response.data?.data?.streamPath;
          if (!streamPath) throw new Error('Unable to start the video playback session.');
          if (!cancelled) setPreviewUrl(api.getUri({ url: streamPath }));
          return;
        }
        const r = await api.get(`/training/course-content/file/${item._id}`, { responseType: 'blob' });
        if (cancelled) return;
        // The backend MAY return JSON (e.g. an error envelope) even when
        // we asked for a blob — axios still wraps the response body as a
        // Blob. Sniff the actual mime type and surface the message instead
        // of silently rendering a JSON blob in the iframe.
        const ct = (r.data?.type || '').toLowerCase();
        if (ct.includes('json') || ct.startsWith('text/')) {
          const text = await r.data.text();
          let msg = text;
          try { msg = JSON.parse(text)?.message || msg; } catch (_) {}
          throw new Error(msg || 'Server returned a non-file response');
        }
        const blob = new Blob([r.data], { type: item.mimeType || r.data.type || 'application/octet-stream' });
        objUrl = URL.createObjectURL(blob);
        setPreviewUrl(objUrl);
      } catch (e) {
        if (!cancelled) {
          // Surface the real reason — 404 (file missing), 403 (not yet
          // approved), 500 (server crashed), or a network error — instead
          // of always saying "Unable to load file preview".
          const status = e.response?.status;
          const serverMsg = e.response?.data?.message;
          let msg = 'Unable to load file preview.';
          if (status === 403) msg = serverMsg || 'You don\'t have permission to view this file.';
          else if (status === 404) msg = serverMsg || 'File missing from server — please re-upload.';
          else if (serverMsg) msg = serverMsg;
          else if (e.message) msg = e.message;
          setLoadError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; if (objUrl) try { URL.revokeObjectURL(objUrl); } catch (_) {} };
  }, [item._id, item.type, item.mimeType, streamVideo]);

  if (loading) {
    return <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary"/><span className="ms-2 text-muted small">Loading preview…</span></div>;
  }
  if (loadError) {
    return <div className="alert alert-warning py-2 small mb-0">{loadError}</div>;
  }

  if (item.type === 'quiz') {
    if (interactiveQuiz) {
      return (
        <LinkedQuizAttempt
          accessContext="course_content"
          lesson={{
            _id: item._id,
            quizRequirement: item.lmsLesson?.quizRequirement || 'optional',
          }}
        />
      );
    }
    return (
      <div className="border rounded p-3 bg-light d-flex align-items-center justify-content-between gap-3 flex-wrap">
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-patch-question text-warning fs-4" />
          <div><div className="fw-semibold">Linked quiz</div><div className="small text-muted">Uses the published quiz selected from the Quiz module.</div></div>
        </div>
        <span className="badge text-bg-light border text-capitalize">{item.lmsLesson?.quizRequirement || 'optional'}</span>
      </div>
    );
  }

  // Notion — structured blocks
  if (item.type === 'notion' && item.contentBlocks?.length > 0) {
    return (
      <div className="p-2" style={{ maxHeight: '70vh', overflow: 'auto' }}>
        {item.contentBlocks.map((block, i) => {
          switch (block.type) {
            case 'heading_1': return <h4 key={i} className="fw-bold mt-3 mb-2">{block.text}</h4>;
            case 'heading_2': return <h5 key={i} className="fw-semibold mt-3 mb-2">{block.text}</h5>;
            case 'heading_3': return <h6 key={i} className="fw-semibold mt-2 mb-1">{block.text}</h6>;
            case 'paragraph':  return <p key={i} className="mb-2">{block.text}</p>;
            case 'bullet':     return <li key={i} className="ms-3 mb-1">{block.text}</li>;
            case 'numbered':   return <li key={i} className="ms-3 mb-1" style={{listStyleType:'decimal'}}>{block.text}</li>;
            case 'code':       return <pre key={i} className="bg-dark text-light p-3 rounded mb-2" style={{fontSize:13}}><code>{block.text}</code></pre>;
            case 'quote':      return <blockquote key={i} className="border-start border-3 border-primary ps-3 my-2 text-muted fst-italic">{block.text}</blockquote>;
            case 'callout':    return <div key={i} className="alert alert-info py-2 d-flex align-items-start gap-2"><span>{block.icon || 'ⓘ'}</span><span>{block.text}</span></div>;
            case 'todo':       return <div key={i} className="form-check mb-1"><input type="checkbox" className="form-check-input" checked={block.checked} readOnly/><label className="form-check-label">{block.text}</label></div>;
            case 'toggle':     return <details key={i} className="mb-2"><summary className="fw-semibold">{block.text}</summary></details>;
            case 'divider':    return <hr key={i}/>;
            case 'image':      return <div key={i} className="mb-2"><img src={withAuthToken(block.url)} alt={block.caption || ''} className="img-fluid rounded" style={{maxHeight:400}}/>{block.caption && <div className="text-muted small mt-1">{block.caption}</div>}</div>;
            case 'table_row':  return <div key={i} className="d-flex gap-2 border-bottom py-1 small">{(block.cells||[]).map((c,j)=><span key={j} className="flex-fill">{c}</span>)}</div>;
            default: return null;
          }
        })}
      </div>
    );
  }

  // Raw HTML
  if (item.type === 'html') {
    return <div dangerouslySetInnerHTML={{ __html: item.htmlContent || '' }} className="p-2" style={{ maxHeight: '70vh', overflow: 'auto' }} />;
  }

  // PDF
  if (item.type === 'pdf' && previewUrl) {
    return (
      <div>
        <iframe src={previewUrl} title={item.title} style={{ width: '100%', height: '65vh', border: '1px solid #e5e7eb', borderRadius: 4 }} />
        <div className="mt-2 d-flex justify-content-end">
          <a href={previewUrl} download={item.title || 'document.pdf'} className="btn btn-sm btn-outline-primary">
            <i className="bi bi-download me-1" />Download
          </a>
        </div>
      </div>
    );
  }

  // Video
  if (item.type === 'video' && previewUrl) {
    return (
      <div className="text-center">
        <video src={previewUrl} controls preload={streamVideo ? 'metadata' : undefined} style={{ width: '100%', maxHeight: '65vh', background: '#000', borderRadius: 4 }}>
          Your browser does not support inline video playback.
        </video>
        {item.description && <div className="text-muted small mt-2">{item.description}</div>}
      </div>
    );
  }

  // Office document — browsers can't render .doc/.ppt natively
  if (item.type === 'document' && previewUrl) {
    return (
      <div>
        <div className="alert alert-info small mb-2">
          <i className="bi bi-info-circle me-1" />
          Office documents can't render directly in the browser.
        </div>
        <a href={previewUrl} download={item.title || 'document'} className="btn btn-primary">
          <i className="bi bi-download me-1" />Download {item.title}
        </a>
      </div>
    );
  }

  // Private internal link — fetch content from backend and render HTML
  if (item.type === 'internal_link' && item.externalUrl?.startsWith('private://')) {
    return <PrivateHtmlViewer filePath={item.externalUrl.replace('private://', '')} />;
  }

  // External / internal link — iframe (may be blocked by X-Frame-Options).
  // Rewrite known video-host URLs to their /embed equivalents first so
  // YouTube / Vimeo / Loom / Google Drive previews actually render.
  if ((item.type === 'link' || item.type === 'internal_link') && item.externalUrl) {
    const embedUrl = toEmbeddableUrl(item.externalUrl);
    const isYouTube = /youtube\.com\/embed\//.test(embedUrl);
    const isVimeo   = /player\.vimeo\.com\/video\//.test(embedUrl);
    const isVideoEmbed = isYouTube || isVimeo || /loom\.com\/embed\//.test(embedUrl);
    return (
      <div>
        <div className="alert alert-light small d-flex justify-content-between align-items-center mb-2">
          <span className="text-truncate"><i className="bi bi-link-45deg me-1" />{item.externalUrl}</span>
          <a href={item.externalUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary flex-shrink-0 ms-2">
            Open in new tab <i className="bi bi-box-arrow-up-right ms-1" />
          </a>
        </div>
        <div style={isVideoEmbed
          ? { position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 4 }
          : undefined}>
          <iframe
            src={embedUrl}
            title={item.title}
            style={isVideoEmbed
              ? { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }
              : { width: '100%', height: '60vh', border: '1px solid #e5e7eb', borderRadius: 4 }}
            allow={isVideoEmbed ? 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen' : undefined}
            allowFullScreen={isVideoEmbed || undefined}
            // Don't sandbox known video embeds — Vimeo/YouTube need cookies for
            // playback. Sandbox stays on for arbitrary external links so we
            // don't grant scripts on those broad permissions.
            sandbox={isVideoEmbed ? undefined : 'allow-scripts allow-same-origin allow-forms allow-popups allow-presentation'}
          />
        </div>
        {!isVideoEmbed && (
          <div className="text-muted small mt-2">
            <i className="bi bi-shield-exclamation me-1" />
            Some sites block embedding. If the preview is blank, use "Open in new tab".
          </div>
        )}
      </div>
    );
  }

  return <div className="text-muted small">Nothing to preview.</div>;
};

// ─── Content Item Row ─────────────────────────────────────────────────────────
// Collapsed by default — user clicks to expand and view the content inline.
// User can collapse with the chevron.
const ContentItemRow = ({ item, canWrite, canAdmin, onApprove, onReject, onDelete, onEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const [sb, sc] = statusBadge(item.status);
  const isApproved = item.status === 'approved';
  const hasLmsLesson = !!item?.lmsLesson?.enabled;

  return (
    <div className={`card mb-2 ${item.status === 'rejected' ? 'border-danger' : ''}`}>
      <div className="card-header d-flex align-items-center gap-2 py-2" style={{ background: '#fafbfc' }}>
        <i className={`bi ${typeIcon(item.type)} text-${typeColor(item.type)} fs-5`}/>
        <div className="flex-grow-1 min-w-0">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="fw-semibold">{item.title}</span>
            <span className={`badge bg-${sc} small`}>{sb}</span>
            {item.isReusable && (
              <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle small" title="Reusable across topics/courses">
                <i className="bi bi-arrow-repeat me-1"/>Reusable
              </span>
            )}
            {hasLmsLesson && (
              <span className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle small" title="Enabled for LMS runtime">
                <i className="bi bi-play-btn me-1"/>LMS Lesson
              </span>
            )}
            {item.fileSize && <span className="text-muted small">{fmtSize(item.fileSize)}</span>}
          </div>
          {item.description && <div className="text-muted small">{item.description}</div>}
          {item.rejectionReason && <div className="text-danger small">Rejected: {item.rejectionReason}</div>}
          {hasLmsLesson && (
            <div className="text-muted small">
              {item.lmsLesson?.durationSeconds ? `${item.lmsLesson.durationSeconds}s` : 'Lesson enabled'}
              {item.lmsLesson?.isPreview ? ' | Preview' : ''}
            </div>
          )}
          <div className="text-muted" style={{fontSize:'0.72rem'}}>
            By {item.uploadedBy?.firstName} {item.uploadedBy?.lastName} · {new Date(item.createdAt).toLocaleDateString()}
          </div>
        </div>
        <div className="d-flex gap-1 flex-shrink-0">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setExpanded(v => !v)}
            title={expanded ? 'Collapse' : 'Expand'}
          >
            <i className={`bi ${expanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
          </button>
          {canWrite && onEdit && (
            <button className="btn btn-outline-primary btn-sm" onClick={() => onEdit(item)} title="Edit content">
              <i className="bi bi-pencil"/>
            </button>
          )}
          {canAdmin && item.status === 'pending' && (
            <>
              <button className="btn btn-outline-success btn-sm" onClick={() => onApprove(item)} title="Approve">
                <i className="bi bi-check-lg"/>
              </button>
              <button className="btn btn-outline-danger btn-sm" onClick={() => onReject(item)} title="Reject">
                <i className="bi bi-x-lg"/>
              </button>
            </>
          )}
          {canWrite && (
            <button className="btn btn-outline-danger btn-sm" onClick={() => onDelete(item)} title="Delete">
              <i className="bi bi-trash"/>
            </button>
          )}
        </div>
      </div>

      {expanded && isApproved && (
        <div className="card-body">
          <InlineContentPreview item={item} />
        </div>
      )}
      {expanded && !isApproved && (
        <div className="card-body py-2">
          <div className="alert alert-warning py-2 small mb-0">
            <i className="bi bi-shield-exclamation me-1" />
            This content is pending approval — preview will be visible once approved.
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Upload Modal ─────────────────────────────────────────────────────────────
const UploadModal = ({ show, onClose, courses, courseId, setCourseId, onUploaded, initialModId='', initialTopId='' }) => {
  const [modules, setModules] = useState([]);
  const [topics, setTopics]   = useState([]);
  const [modId,  setModId]    = useState('');
  const [topId,  setTopId]    = useState('');
  const [title,  setTitle]    = useState('');
  const [desc,   setDesc]     = useState('');
  const [type,   setType]     = useState('pdf');
  const [url,    setUrl]      = useState('');
  const [html,   setHtml]     = useState('');
  const [file,   setFile]     = useState(null);
  const [isReusable, setIsReusable] = useState(true);
  const [isLmsLesson, setIsLmsLesson] = useState(false);
  const [lmsThumbnailUrl, setLmsThumbnailUrl] = useState('');
  const [lmsDurationSeconds, setLmsDurationSeconds] = useState('');
  const [lmsAvailableFromPlan, setLmsAvailableFromPlan] = useState('');
  const [lmsRequiresItemIds, setLmsRequiresItemIds] = useState([]);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailDimensions, setThumbnailDimensions] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [prerequisiteOptions, setPrerequisiteOptions] = useState([]);
  const [loadingPrerequisites, setLoadingPrerequisites] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [quizzesLoading, setQuizzesLoading] = useState(false);
  const [lmsQuizId, setLmsQuizId] = useState('');
  const [lmsQuizRequirement, setLmsQuizRequirement] = useState('optional');
  const [saving, setSaving]   = useState(false);
  const [err,    setErr]      = useState('');

  // Inline "create new module/topic" state
  const [newModTitle, setNewModTitle] = useState('');
  const [newTopTitle, setNewTopTitle] = useState('');
  const [creating, setCreating]       = useState(false);

  // Stores a pending topId to apply after the [modId] cascade loads topics
  const pendingTopIdRef = useRef('');

  // Notion preview state
  const [notionPreview, setNotionPreview] = useState(null);   // { title, blocks, blockCount } | null
  const [previewing, setPreviewing] = useState(false);

  // Reusable Bank mode
  const [uploadMode, setUploadMode]               = useState('upload'); // 'upload' | 'reusable'
  const [reusTopics, setReusTopics]               = useState([]);
  const [reusLoading, setReusLoading]             = useState(false);
  const [selectedReusable, setSelectedReusable]   = useState(null);
  // Search + pagination for reusable bank
  const [reusSearch,    setReusSearch]            = useState('');
  const [reusPage,      setReusPage]              = useState(1);
  const [reusAllTopics, setReusAllTopics]         = useState([]); // full unfiltered list for search & pagination

  // Private file browser (internal_link type)
  const [privateFilePath, setPrivateFilePath] = useState('');
  const [previewHtml, setPreviewHtml]         = useState('');
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const [fileBrowserOpen, setFileBrowserOpen] = useState(false);
  const [browserPath, setBrowserPath]         = useState('');
  const [browserItems, setBrowserItems]       = useState([]);
  const [browserLoading, setBrowserLoading]   = useState(false);
  const showVideoLessonFields = type === 'video';
  const showQuizLessonFields = type === 'quiz';

  useEffect(() => {
    if (!show || !showQuizLessonFields) return;
    let cancelled = false;
    setQuizzesLoading(true);
    api.get('/quiz', { params: { limit: 500 } })
      .then((response) => {
        if (cancelled) return;
        setQuizzes(response.data?.records || response.data?.data?.records || response.data?.data || []);
      })
      .catch(() => { if (!cancelled) setQuizzes([]); })
      .finally(() => { if (!cancelled) setQuizzesLoading(false); });
    return () => { cancelled = true; };
  }, [show, showQuizLessonFields]);

  useEffect(() => {
    if (!showQuizLessonFields || !lmsQuizId) return;
    const selectedQuiz = quizzes.find((quiz) => String(quiz._id) === String(lmsQuizId));
    if (selectedQuiz?.title) setTitle(selectedQuiz.title);
  }, [showQuizLessonFields, lmsQuizId, quizzes]);

  const loadModules = async () => {
    if (!courseId) return;
    try {
      const r = await api.get(`/training/course-content/modules/${courseId}`);
      setModules(r.data?.records || r.data?.data || []);
    } catch (_) { setModules([]); }
  };
  const loadTopics = async () => {
    if (!modId) return;
    try {
      const r = await api.get(`/training/course-content/topics/${modId}`);
      setTopics(r.data?.records || r.data?.data || []);
    } catch (_) { setTopics([]); }
  };

  // When course changes: reload modules and reset downstream selections.
  useEffect(() => {
    if (!courseId) { setModules([]); setModId(''); setTopics([]); setTopId(''); return; }
    loadModules().then(() => { setModId(''); setTopics([]); setTopId(''); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  // When module selection changes: reload topics.
  useEffect(() => {
    if (!modId) { setTopics([]); setTopId(''); pendingTopIdRef.current = ''; return; }
    const pending = pendingTopIdRef.current;
    pendingTopIdRef.current = '';
    loadTopics().then(() => setTopId(pending || ''));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modId]);

  // Re-fetch whenever the modal is opened so the lists are always fresh
  // (covers the case where modules/topics were added via StructureManager
  // after the last course selection, or where courseId was already set when
  // the modal opened so the [courseId] effect didn't fire again).
  useEffect(() => {
    if (!show) return;
    setIsReusable(true);
    setUploadMode('upload');
    setSelectedReusable(null);
    setFile(null);
    setUploadProgress(0);
    setErr('');
    setPrivateFilePath('');
    setPreviewHtml('');
    setIsLmsLesson(false);
    setLmsThumbnailUrl('');
    setThumbnailDimensions(null);
    setLmsDurationSeconds('');
    setLmsAvailableFromPlan('');
    setLmsRequiresItemIds([]);
    setPrerequisiteOptions([]);
    setFileBrowserOpen(false);
    setBrowserPath('');
    if (courseId) loadModules();
    if (initialModId) {
      pendingTopIdRef.current = initialTopId || '';
      if (initialModId === modId) {
        // modId unchanged — [modId] effect won't fire; set topId directly
        setTopId(initialTopId || '');
        pendingTopIdRef.current = '';
        if (modId) loadTopics();
      } else {
        setModId(initialModId);
        // [modId] effect will fire and consume pendingTopIdRef
      }
    } else if (modId) {
      loadTopics();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Reset preview when Notion URL or type changes
  useEffect(() => { setNotionPreview(null); }, [url, type]);

  // Video lessons use their selected topic as the default title. The title
  // remains editable after it is populated.
  useEffect(() => {
    if (type !== 'video' || !topId) return;
    const selectedTopic = topics.find((topic) => String(topic._id) === String(topId));
    if (selectedTopic?.title) setTitle(selectedTopic.title);
  }, [type, topId, topics]);

  useEffect(() => {
    if (!show || !courseId || !showVideoLessonFields) return;
    let cancelled = false;
    const loadPrerequisites = async () => {
      setLoadingPrerequisites(true);
      try {
        const curriculumRes = await api.get(`/training/course-content/curriculum/${courseId}`);
        const curriculum = curriculumRes.data?.data || curriculumRes.data?.records || [];
        const topicIds = curriculum.flatMap((mod) => (mod.topics || []).map((topic) => ({
          topicId: topic._id,
          topicTitle: topic.title,
          moduleTitle: mod.title,
        })));
        const itemResponses = await Promise.all(topicIds.map((entry) =>
          api.get(`/training/course-content/topic-items/${entry.topicId}`).then((res) => ({
            ...entry,
            items: res.data?.data?.items || [],
          })).catch(() => ({ ...entry, items: [] }))
        ));
        if (cancelled) return;
        const options = itemResponses.flatMap((entry) => (entry.items || []).map((item) => ({
          value: String(item._id),
          label: `${entry.moduleTitle} > ${entry.topicTitle} > ${item.title}`,
        })));
        setPrerequisiteOptions(options);
      } catch (_) {
        if (!cancelled) setPrerequisiteOptions([]);
      }
      if (!cancelled) setLoadingPrerequisites(false);
    };
    loadPrerequisites();
    return () => { cancelled = true; };
  }, [show, courseId, showVideoLessonFields]);

  // Fetch & group reusable topics when switching to reusable mode
  useEffect(() => {
    if (uploadMode !== 'reusable') return;
    setReusLoading(true);
    api.get('/training/course-content/reusable')
      .then(r => {
        const items = r.data?.data?.items || [];
        const groups = {};
        items.forEach(item => {
          const tid = item.topicId?._id;
          if (!tid) return;
          if (!groups[tid]) groups[tid] = {
            topicId: tid,
            topicTitle: item.topicId?.title || '—',
            moduleTitle: item.moduleId?.title || '—',
            courseName: item.courseId?.courseName || '—',
            courseColor: item.courseId?.courseColor || '',
            itemCount: 0,
          };
          groups[tid].itemCount++;
        });
        const all = Object.values(groups);
        setReusAllTopics(all);
        setReusTopics(all); // show first page
        setReusPage(1);
      })
      .catch(() => { setReusAllTopics([]); setReusTopics([]); })
      .finally(() => setReusLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadMode]);

  // Apply search filter + pagination to reusable bank
  useEffect(() => {
    const search = reusSearch.trim().toLowerCase();
    const filtered = search
      ? reusAllTopics.filter(g =>
          (g.topicTitle || '').toLowerCase().includes(search) ||
          (g.moduleTitle || '').toLowerCase().includes(search) ||
          (g.courseName || '').toLowerCase().includes(search)
        )
      : reusAllTopics;
    setReusTopics(filtered);
    setReusPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reusSearch, reusAllTopics]);

  const REUS_PAGE_SIZE = 10;
  const reusTotalPages = Math.max(1, Math.ceil(reusTopics.length / REUS_PAGE_SIZE));
  const reusPageStart = (reusPage - 1) * REUS_PAGE_SIZE;
  const reusPagedTopics = reusTopics.slice(reusPageStart, reusPageStart + REUS_PAGE_SIZE);

  const handleReusableSubmit = async () => {
    if (!topId) { setErr('Select a topic first.'); return; }
    if (!selectedReusable) { setErr('Select a topic from the reusable bank.'); return; }
    setSaving(true); setErr('');
    try {
      await api.post('/training/course-content/copy-reusable-to-topic', {
        sourceTopicId: selectedReusable.topicId,
        targetCourseId: courseId,
        targetModuleId: modId,
        targetTopicId: topId,
      });
      onUploaded(); onClose();
    } catch(e) { setErr(e.response?.data?.message || 'Failed to copy content.'); }
    setSaving(false);
  };

  const openBrowser = async (fPath = '') => {
    setFileBrowserOpen(true);
    setBrowserPath(fPath);
    setBrowserLoading(true);
    try {
      const r = await api.get('/training/course-content/private-files', { params: { folderPath: fPath } });
      setBrowserItems(r.data?.data?.items || []);
    } catch(_) { setBrowserItems([]); }
    setBrowserLoading(false);
  };

  const handlePrivatePreview = async () => {
    if (!privateFilePath) return;
    setFetchingPreview(true); setErr('');
    try {
      const r = await api.get('/training/course-content/private-file-content', { params: { filePath: privateFilePath } });
      setPreviewHtml(r.data?.data?.content || '');
    } catch(e) { setErr(e.response?.data?.message || 'Failed to load file.'); }
    setFetchingPreview(false);
  };

  const handleOpenEditor = () => {
    const key = Date.now().toString();
    try { localStorage.setItem(`html_editor_input_${key}`, previewHtml); } catch (_) {}
    window.open(`/html-editor?key=${key}`, '_blank');
    const onMsg = (e) => {
      if (e.data?.type === 'html_editor_done' && e.data?.key === key) {
        // Editor sends HTML directly in the message payload — no localStorage size limit
        const html = e.data.html ?? localStorage.getItem(`html_editor_result_${key}`) ?? previewHtml;
        setPreviewHtml(html);
        try { localStorage.removeItem(`html_editor_input_${key}`); } catch (_) {}
        try { localStorage.removeItem(`html_editor_result_${key}`); } catch (_) {}
        window.removeEventListener('message', onMsg);
      }
    };
    window.addEventListener('message', onMsg);
  };

  const createModule = async () => {
    if (!newModTitle.trim() || !courseId) return;
    setCreating(true); setErr('');
    try {
      const r = await api.post('/training/course-content/modules', { courseId, title: newModTitle.trim() });
      const newMod = r.data?.data || r.data;
      await loadModules();
      if (newMod?._id) setModId(newMod._id);
      setNewModTitle('');
    } catch (e) { setErr(e.response?.data?.message || 'Failed to create module'); }
    setCreating(false);
  };

  const createTopic = async () => {
    if (!newTopTitle.trim() || !modId) return;
    setCreating(true); setErr('');
    try {
      const r = await api.post('/training/course-content/topics', { courseId, moduleId: modId, title: newTopTitle.trim() });
      const newTop = r.data?.data || r.data;
      await loadTopics();
      if (newTop?._id) setTopId(newTop._id);
      setNewTopTitle('');
    } catch (e) { setErr(e.response?.data?.message || 'Failed to create topic'); }
    setCreating(false);
  };

  const handleNotionPreview = async () => {
    if (!url.trim()) { setErr('Enter a Notion page URL first.'); return; }
    setPreviewing(true); setErr('');
    try {
      const r = await api.post('/training/course-content/import-notion/preview', { notionUrl: url.trim() });
      setNotionPreview(r.data?.data || r.data);
    } catch (e) { setErr(e.response?.data?.message || 'Preview failed. Check the URL and that the page is shared with the integration.'); }
    setPreviewing(false);
  };

  const handleLmsThumbnailUpload = async (fileToUpload) => {
    if (!fileToUpload) return;
    if (fileToUpload.size > 2 * 1024 * 1024) {
      setErr('Thumbnail must be 2 MB or smaller.');
      return;
    }
    if (!isSupportedThumbnail(fileToUpload)) {
      setErr('Use a JPG, PNG, GIF, WEBP, or SVG thumbnail.');
      return;
    }
    setErr('');
    setThumbnailUploading(true);
    try {
      setThumbnailDimensions(await readImageDimensions(fileToUpload));
      const formData = new FormData();
      formData.append('file', fileToUpload);
      const response = await uploadLessonThumbnail(formData);
      const uploadedUrl = response?.data?.url || response?.url || '';
      if (!uploadedUrl) throw new Error('Thumbnail upload failed.');
      setLmsThumbnailUrl(uploadedUrl);
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Thumbnail upload failed.');
    } finally {
      setThumbnailUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!courseId || !modId || !topId || !type) { setErr('Course, module, topic and type are required.'); return; }
    if (type !== 'notion' && !title) { setErr('Title is required.'); return; }
    if (type === 'notion' && !url) { setErr('Notion URL is required.'); return; }
    if (type === 'notion' && !notionPreview) { setErr('Click "Preview" first so you can review the content before uploading.'); return; }
    if (type === 'link' && !url) { setErr('URL is required.'); return; }
    if (type === 'internal_link' && !privateFilePath) { setErr('Select a private HTML file using the Browse button.'); return; }
    if (type === 'video' && !file) { setErr('Select a video file before uploading.'); return; }
    if (type === 'video' && !VIDEO_EXTENSIONS.has(fileExtension(file))) { setErr('Use an MP4, WEBM, MOV, AVI, MKV, MPEG, or MPG video.'); return; }
    if (type === 'video' && file.size > 300 * 1024 * 1024) { setErr('Video must be 300 MB or smaller.'); return; }
    if (type === 'quiz' && !lmsQuizId) { setErr('Select a published quiz.'); return; }
    if (thumbnailUploading) { setErr('Wait for the thumbnail upload to finish.'); return; }
    setSaving(true); setErr('');
    try {
        if (type === 'notion') {
          await api.post('/training/course-content/import-notion', {
            courseId, moduleId: modId, topicId: topId, notionUrl: url,
            isReusable,
            lmsLessonEnabled: showVideoLessonFields,
            lmsLessonType: type === 'notion' ? 'text' : (type === 'video' ? 'uploaded_video' : ''),
            lmsThumbnailUrl,
            lmsDurationSeconds,
            lmsIsPreview: false,
            lmsAvailableFromPlan,
            lmsRequiresItemIds: lmsRequiresItemIds.join(','),
          });
        } else {
        const fd = new FormData();
        fd.append('courseId', courseId); fd.append('moduleId', modId); fd.append('topicId', topId);
        fd.append('title', title); fd.append('description', desc); fd.append('type', type);
        if (type === 'link') fd.append('externalUrl', url);
        if (type === 'internal_link') fd.append('externalUrl', 'private://' + privateFilePath);
        if (type === 'html') fd.append('htmlContent', html);
        if (file) fd.append('file', file);
        fd.append('isReusable', isReusable ? 'true' : 'false');
        fd.append('lmsLessonEnabled', (showVideoLessonFields || showQuizLessonFields) ? 'true' : 'false');
        fd.append('lmsLessonType', type === 'video' ? 'uploaded_video' : type === 'quiz' ? 'linked_quiz' : type === 'html' ? 'text' : type === 'notion' ? 'text' : '');
        if (type === 'quiz') {
          fd.append('lmsQuizId', lmsQuizId);
          fd.append('lmsQuizRequirement', lmsQuizRequirement);
        }
        fd.append('lmsThumbnailUrl', lmsThumbnailUrl);
        fd.append('lmsDurationSeconds', lmsDurationSeconds);
        fd.append('lmsIsPreview', 'false');
        fd.append('lmsAvailableFromPlan', lmsAvailableFromPlan);
        fd.append('lmsRequiresItemIds', lmsRequiresItemIds.join(','));
        setUploadProgress(0);
        await uploadFile('/training/course-content/upload', fd, setUploadProgress);
      }
      onUploaded(); onClose();
    } catch(e) {
      const fallback = e.code === 'ECONNABORTED'
        ? 'Upload timed out. Check your connection and try again.'
        : 'Upload failed.';
      setErr(e.response?.data?.message || fallback);
    }
    setSaving(false);
    setUploadProgress(0);
  };

  return (
    <BSModal show={show} title="Upload Content" size="lg" onClose={onClose}
      footer={<>
        {/* Error pinned in the footer so it's always visible even when scrolled down */}
        {err && (
          <div className="alert alert-danger py-1 px-2 small mb-0 me-auto d-flex align-items-center gap-2" style={{maxWidth:'60%'}}>
            <i className="bi bi-exclamation-triangle-fill flex-shrink-0"/>
            <span>{err}</span>
          </div>
        )}
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
        {uploadMode === 'reusable' ? (
          <button className="btn btn-success btn-sm" onClick={handleReusableSubmit} disabled={saving || !selectedReusable || !topId}>
            {saving ? <><span className="spinner-border spinner-border-sm me-1"/>Copying…</> : <><i className="bi bi-arrow-repeat me-1"/>Use This Content</>}
          </button>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={saving || thumbnailUploading}>
            {saving ? <><span className="spinner-border spinner-border-sm me-1"/>Uploading{uploadProgress ? ` ${uploadProgress}%` : ''}…</> : 'Upload'}
          </button>
        )}
      </>}
    >
      {/* Keep a top-of-form error too for users who haven't scrolled */}
      {err && <div className="alert alert-danger py-2 d-flex align-items-center gap-2"><i className="bi bi-exclamation-triangle-fill"/><span>{err}</span></div>}
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">Course <span className="text-danger">*</span></label>
          <select className="form-select form-select-sm" value={courseId} onChange={e => setCourseId(e.target.value)}>
            <option value="">Select Course</option>
            {courses.map(c => <option key={c._id} value={c._id}>{c.courseName || c.name}</option>)}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label">Module <span className="text-danger">*</span></label>
          <select className="form-select form-select-sm" value={modId} onChange={e => setModId(e.target.value)} disabled={!courseId}>
            <option value="">Select Module</option>
            {modules.map(m => <option key={m._id} value={m._id}>{m.title}</option>)}
          </select>
          {courseId && modules.length === 0 && (
            <div className="mt-2 p-2 border rounded bg-warning-subtle small">
              <div className="text-warning-emphasis mb-1">
                <i className="bi bi-exclamation-triangle me-1" />
                No modules yet. Create one below or use the <strong>Structure</strong> tab.
              </div>
              <div className="input-group input-group-sm">
                <input className="form-control form-control-sm" placeholder="e.g. Module 1 — Introduction"
                       value={newModTitle} onChange={e => setNewModTitle(e.target.value)} />
                <button className="btn btn-sm btn-primary" onClick={createModule} disabled={creating || !newModTitle.trim()}>
                  {creating ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-plus" />Create</>}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="col-md-6">
          <label className="form-label">Topic <span className="text-danger">*</span></label>
          <select className="form-select form-select-sm" value={topId} onChange={e => setTopId(e.target.value)} disabled={!modId}>
            <option value="">Select Topic</option>
            {topics.map(t => <option key={t._id} value={t._id}>{t.title}</option>)}
          </select>
          {modId && topics.length === 0 && (
            <div className="mt-2 p-2 border rounded bg-warning-subtle small">
              <div className="text-warning-emphasis mb-1">
                <i className="bi bi-exclamation-triangle me-1" />
                This module has no topics yet. Create one:
              </div>
              <div className="input-group input-group-sm">
                <input className="form-control form-control-sm" placeholder="e.g. Topic 1 — Overview"
                       value={newTopTitle} onChange={e => setNewTopTitle(e.target.value)} />
                <button className="btn btn-sm btn-primary" onClick={createTopic} disabled={creating || !newTopTitle.trim()}>
                  {creating ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-plus" />Create</>}
                </button>
              </div>
            </div>
          )}
        </div>
        {/* Mode toggle */}
        <div className="col-12">
          <div className="btn-group w-100" role="group">
            <button type="button"
              className={`btn btn-sm ${uploadMode === 'upload' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => { setUploadMode('upload'); setSelectedReusable(null); }}>
              <i className="bi bi-upload me-1"/>Upload New Content
            </button>
            <button type="button"
              className={`btn btn-sm ${uploadMode === 'reusable' ? 'btn-success' : 'btn-outline-success'}`}
              onClick={() => setUploadMode('reusable')}>
              <i className="bi bi-arrow-repeat me-1"/>From Reusable Bank
            </button>
          </div>
        </div>

        {/* Reusable Bank: topic cards */}
        {uploadMode === 'reusable' && (
          <div className="col-12">
            {/* Search bar */}
            <div className="d-flex gap-2 align-items-center mb-2">
              <div className="position-relative flex-grow-1">
                <i className="bi bi-search position-absolute" style={{ left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}/>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ paddingLeft: 30 }}
                  placeholder="Search reusable topics by title, module, or course..."
                  value={reusSearch}
                  onChange={e => setReusSearch(e.target.value)}/>
              </div>
              <span className="badge bg-light text-secondary border" style={{ fontSize: '0.75rem' }}>
                {reusTopics.length} match{reusTopics.length !== 1 ? 'es' : ''}
              </span>
            </div>

            {reusLoading ? (
              <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-success"/></div>
            ) : reusTopics.length === 0 ? (
              <div className="alert alert-warning py-2 small mb-0">
                <i className="bi bi-exclamation-triangle me-1"/>
                {reusSearch ? `No reusable content matches "${reusSearch}". Try a different search.` : 'No reusable content in the bank yet. Upload content first.'}
              </div>
            ) : (
              <>
                <div className="row g-2">
                  {reusPagedTopics.map(g => (
                    <div key={g.topicId} className="col-md-6 col-lg-4">
                      <div
                        className={`card h-100 ${selectedReusable?.topicId === g.topicId ? 'border-success border-2 bg-success-subtle' : 'border'}`}
                        role="button"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedReusable(g)}
                      >
                        <div className="card-body py-2 px-3">
                          <div className="d-flex justify-content-between align-items-start">
                            <span className="fw-semibold small flex-grow-1 me-1">{g.topicTitle}</span>
                            {selectedReusable?.topicId === g.topicId && (
                              <i className="bi bi-check-circle-fill text-success flex-shrink-0"/>
                            )}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            <i className="bi bi-diagram-3 me-1"/>
                            <CourseNameBadge courseName={g.courseName} courseColor={g.courseColor} compact className="me-1" /><span>› {g.moduleTitle}</span>
                          </div>
                          <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle small mt-1">
                            {g.itemCount} item{g.itemCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {reusTotalPages > 1 && (
                  <nav className="d-flex justify-content-between align-items-center mt-2" aria-label="Reusable bank pagination">
                    <small className="text-muted">
                      Showing {reusPageStart + 1}-{Math.min(reusPageStart + REUS_PAGE_SIZE, reusTopics.length)} of {reusTopics.length}
                    </small>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${reusPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setReusPage(1)} disabled={reusPage === 1}>
                          <i className="bi bi-chevron-double-left"/>
                        </button>
                      </li>
                      <li className={`page-item ${reusPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setReusPage(p => Math.max(1, p - 1))} disabled={reusPage === 1}>
                          <i className="bi bi-chevron-left"/>
                        </button>
                      </li>
                      {Array.from({ length: reusTotalPages }, (_, idx) => idx + 1).map(p => (
                        <li key={p} className={`page-item ${p === reusPage ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setReusPage(p)}>{p}</button>
                        </li>
                      ))}
                      <li className={`page-item ${reusPage === reusTotalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setReusPage(p => Math.min(reusTotalPages, p + 1))} disabled={reusPage === reusTotalPages}>
                          <i className="bi bi-chevron-right"/>
                        </button>
                      </li>
                      <li className={`page-item ${reusPage === reusTotalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setReusPage(reusTotalPages)} disabled={reusPage === reusTotalPages}>
                          <i className="bi bi-chevron-double-right"/>
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </>
            )}
            {selectedReusable && (
              <div className="alert alert-success py-2 small mb-0 mt-2">
                <i className="bi bi-arrow-repeat me-1"/>
                <strong>{selectedReusable.itemCount}</strong> item{selectedReusable.itemCount !== 1 ? 's' : ''} from
                <strong> {selectedReusable.topicTitle}</strong> will be copied into the selected topic.
                {!topId && <span className="text-warning ms-2"><i className="bi bi-exclamation-triangle me-1"/>Select a topic above first.</span>}
              </div>
            )}
          </div>
        )}

        {/* Upload fields */}
        {uploadMode === 'upload' && <>
        <div className="col-md-6">
          <label className="form-label">Type <span className="text-danger">*</span></label>
          <select className="form-select form-select-sm" value={type} onChange={e => { setType(e.target.value); setFile(null); setErr(''); }}>
            {['pdf','video','document','link','internal_link','html','notion','quiz'].map(t => <option key={t} value={t}>{t === 'notion' ? 'Notion Import' : t === 'quiz' ? 'Quiz' : t}</option>)}
          </select>
        </div>
        <div className="col-12">
          <label className="form-label">Title <span className="text-danger">*</span></label>
          <input className="form-control form-control-sm" value={title} onChange={e => setTitle(e.target.value)}/>
        </div>
        {showQuizLessonFields && (
          <div className="col-12">
            <div className="border rounded p-3 bg-light">
              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label">Quiz <span className="text-danger">*</span></label>
                  <select className="form-select form-select-sm" value={lmsQuizId} disabled={quizzesLoading} onChange={(event) => setLmsQuizId(event.target.value)}>
                    <option value="">{quizzesLoading ? 'Loading quizzes...' : 'Select a published quiz'}</option>
                    {quizzes.map((quiz) => (
                      <option key={quiz._id} value={quiz._id} disabled={quiz.status !== 'published'}>
                        {quiz.title} ({quiz.category || 'practice'}{quiz.subject ? ` - ${quiz.subject}` : ''}){quiz.status !== 'published' ? ` - ${quiz.status}` : ''}
                      </option>
                    ))}
                  </select>
                  {!quizzesLoading && !quizzes.length && <div className="form-text text-warning">Create and publish a quiz in the Quiz module first.</div>}
                </div>
                <div className="col-md-4">
                  <label className="form-label">Attendance</label>
                  <select className="form-select form-select-sm" value={lmsQuizRequirement} onChange={(event) => setLmsQuizRequirement(event.target.value)}>
                    <option value="optional">Optional</option>
                    <option value="compulsory">Compulsory</option>
                  </select>
                  <div className="form-text">Compulsory quizzes must be submitted before the next lesson.</div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="col-12">
          <label className="form-label">Description</label>
          <input className="form-control form-control-sm" value={desc} onChange={e => setDesc(e.target.value)}/>
        </div>
        <div className="col-12">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="cc-reusable"
              checked={isReusable}
              onChange={e => setIsReusable(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="cc-reusable">
              <i className="bi bi-arrow-repeat me-1 text-success"/>
              Mark as <strong>reusable</strong>
              <span className="text-muted ms-1">— teammates can clone this into other topics/courses.</span>
            </label>
          </div>
        </div>
        {showVideoLessonFields && (
          <div className="col-12">
            <div className="border rounded p-3" style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Thumbnail Image <span className="text-muted fw-normal">(Optional)</span></label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.jfif,.png,.gif,.webp,.svg,image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                      className="form-control form-control-sm"
                      disabled={saving || thumbnailUploading}
                      onChange={async (e) => {
                        const selected = e.target.files?.[0];
                        if (selected) await handleLmsThumbnailUpload(selected);
                      }}
                    />
                    <div className={`form-text ${lmsThumbnailUrl && !thumbnailUploading ? 'text-success fw-semibold' : ''}`}>
                      {thumbnailUploading
                        ? 'Uploading thumbnail...'
                        : (lmsThumbnailUrl
                          ? `Thumbnail uploaded successfully${thumbnailDimensions ? ` (${thumbnailDimensions.width}x${thumbnailDimensions.height})` : ''}.`
                          : 'Optional. Recommended: 1280x720 JPG/PNG, under 2 MB.')}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Duration (seconds)</label>
                    <input type="number" min="0" className="form-control form-control-sm" value={lmsDurationSeconds} onChange={e => setLmsDurationSeconds(e.target.value)} />
                    <div className="form-text">Video upload limit: 300 MB. Enter the lesson duration in seconds.</div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Available From Plan</label>
                    <input className="form-control form-control-sm" value={lmsAvailableFromPlan} onChange={e => setLmsAvailableFromPlan(e.target.value)} placeholder="Basic / Premium / Custom" />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Prerequisite Lessons</label>
                    {loadingPrerequisites ? (
                      <div className="text-muted small"><span className="spinner-border spinner-border-sm me-2" />Loading available lessons…</div>
                    ) : prerequisiteOptions.length ? (
                      <div className="border rounded p-2 bg-white" style={{ maxHeight: 220, overflowY: 'auto' }}>
                        <div className="d-flex flex-column gap-2">
                          {prerequisiteOptions.map((option) => {
                            const checked = lmsRequiresItemIds.includes(option.value);
                            return (
                              <label key={`prereq-${option.value}`} className="form-check border rounded p-2">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    setLmsRequiresItemIds((prev) => (
                                      e.target.checked
                                        ? [...prev, option.value]
                                        : prev.filter((value) => value !== option.value)
                                    ));
                                  }}
                                />
                                <span className="form-check-label ms-2 small">{option.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted small">No other content items are available yet for prerequisites.</div>
                    )}
                  </div>
                </div>
            </div>
          </div>
        )}
        {type === 'link' && (
          <div className="col-12">
            <label className="form-label">URL <span className="text-danger">*</span></label>
            <input className="form-control form-control-sm" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..."/>
          </div>
        )}
        {type === 'internal_link' && (
          <div className="col-12">
            <label className="form-label fw-semibold">Private HTML File <span className="text-danger">*</span></label>
            <div className="d-flex gap-2 mb-2">
              <input className="form-control form-control-sm" value={privateFilePath || ''} readOnly
                placeholder="No file selected — click Browse to pick an HTML file from the server"/>
              <button type="button" className="btn btn-outline-secondary btn-sm flex-shrink-0"
                onClick={() => openBrowser('')}>
                <i className="bi bi-folder2-open me-1"/>Browse
              </button>
              {privateFilePath && (
                <button type="button" className="btn btn-outline-primary btn-sm flex-shrink-0"
                  onClick={handlePrivatePreview} disabled={fetchingPreview}>
                  {fetchingPreview
                    ? <span className="spinner-border spinner-border-sm"/>
                    : <><i className="bi bi-eye me-1"/>Preview</>}
                </button>
              )}
            </div>
            {previewHtml && (
              <div className="border rounded overflow-hidden">
                <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light border-bottom">
                  <span className="small text-muted"><i className="bi bi-file-earmark-code me-1"/>Preview — {privateFilePath}</span>
                  <button type="button" className="btn btn-outline-primary btn-sm py-0 px-2"
                    onClick={handleOpenEditor}>
                    <i className="bi bi-pencil-square me-1"/>Edit in WYSIWYG
                  </button>
                </div>
                <div className="p-3" style={{ maxHeight: '40vh', overflowY: 'auto' }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </div>
            )}

            {/* File Browser Modal */}
            {fileBrowserOpen && (
              <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                <div className="modal-dialog modal-dialog-scrollable">
                  <div className="modal-content">
                    <div className="modal-header py-2">
                      <h6 className="modal-title">
                        <i className="bi bi-folder2-open me-2 text-warning"/>
                        Browse Private HTML Files
                      </h6>
                      <button className="btn-close btn-sm" onClick={() => setFileBrowserOpen(false)}/>
                    </div>
                    <div className="modal-body p-2">
                      {browserPath && (
                        <button className="btn btn-link btn-sm text-decoration-none mb-2 p-0"
                          onClick={() => {
                            const parent = browserPath.includes('/') ? browserPath.split('/').slice(0, -1).join('/') : '';
                            openBrowser(parent);
                          }}>
                          <i className="bi bi-arrow-left me-1"/>Back
                        </button>
                      )}
                      {browserPath && (
                        <div className="small text-muted mb-2 ps-1">
                          <i className="bi bi-folder-fill text-warning me-1"/>
                          {browserPath}
                        </div>
                      )}
                      {browserLoading ? (
                        <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"/></div>
                      ) : browserItems.length === 0 ? (
                        <div className="text-center py-4 text-muted small">
                          <i className="bi bi-folder2-open fs-3 d-block mb-2"/>
                          This folder is empty. Add .html files to <code>backend/private-html/</code>
                        </div>
                      ) : (
                        <div className="list-group list-group-flush">
                          {browserItems.map(item => (
                            <button
                              key={item.path}
                              type="button"
                              className={`list-group-item list-group-item-action d-flex align-items-center gap-2 py-2 px-3 ${!item.isDirectory && privateFilePath === item.path ? 'active' : ''}`}
                              onClick={() => {
                                if (item.isDirectory) { openBrowser(item.path); }
                                else { setPrivateFilePath(item.path); setFileBrowserOpen(false); setPreviewHtml(''); }
                              }}
                            >
                              <i className={`bi ${item.isDirectory ? 'bi-folder-fill text-warning' : 'bi-file-earmark-code text-primary'}`}/>
                              <span className="small">{item.name}</span>
                              {item.isDirectory && <i className="bi bi-chevron-right ms-auto small text-muted"/>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {type === 'notion' && (
          <div className="col-12">
            <label className="form-label">Notion Page URL <span className="text-danger">*</span></label>
            <div className="input-group input-group-sm">
              <input className="form-control form-control-sm" value={url} onChange={e => setUrl(e.target.value)}
                placeholder="https://www.notion.so/your-page-id..."/>
              <button className="btn btn-outline-primary btn-sm" type="button" onClick={handleNotionPreview} disabled={previewing || !url.trim()}>
                {previewing ? <><span className="spinner-border spinner-border-sm me-1"/>Fetching…</> : <><i className="bi bi-eye me-1"/>Preview</>}
              </button>
            </div>
            <div className="form-text">The page must be shared with the Notion integration. Click <strong>Preview</strong> to review the extracted content before upload.</div>

            {notionPreview && (
              <div className="mt-3 border rounded p-2" style={{ background: '#fafbfc' }}>
                <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                  <div>
                    <div className="fw-semibold">
                      <i className="bi bi-check-circle-fill text-success me-1"/>
                      {notionPreview.title}
                    </div>
                    <div className="small text-muted">{notionPreview.blockCount} content blocks extracted</div>
                  </div>
                  <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setNotionPreview(null)}>
                    <i className="bi bi-arrow-clockwise me-1"/>Re-fetch
                  </button>
                </div>
                <div style={{ maxHeight: '40vh', overflow: 'auto', fontSize: 13 }} className="p-2">
                  {(notionPreview.blocks || []).map((block, i) => {
                    switch (block.type) {
                      case 'heading_1': return <h5 key={i} className="fw-bold mt-2 mb-1">{block.text}</h5>;
                      case 'heading_2': return <h6 key={i} className="fw-semibold mt-2 mb-1">{block.text}</h6>;
                      case 'heading_3': return <div key={i} className="fw-semibold mt-1 mb-1">{block.text}</div>;
                      case 'paragraph':  return <p key={i} className="mb-1">{block.text}</p>;
                      case 'bullet':     return <li key={i} className="ms-3">{block.text}</li>;
                      case 'numbered':   return <li key={i} className="ms-3" style={{listStyleType:'decimal'}}>{block.text}</li>;
                      case 'code':       return <pre key={i} className="bg-dark text-light p-2 rounded mb-1" style={{fontSize:11}}><code>{block.text}</code></pre>;
                      case 'quote':      return <blockquote key={i} className="border-start border-3 border-primary ps-2 my-1 text-muted fst-italic small">{block.text}</blockquote>;
                      case 'callout':    return <div key={i} className="alert alert-info py-1 my-1 small">{block.icon || 'ⓘ'} {block.text}</div>;
                      case 'todo':       return <div key={i} className="form-check mb-1 small"><input type="checkbox" className="form-check-input" checked={block.checked} readOnly/><label className="form-check-label">{block.text}</label></div>;
                      case 'toggle':     return <details key={i} className="mb-1"><summary className="fw-semibold small">{block.text}</summary></details>;
                      case 'divider':    return <hr key={i}/>;
                      case 'image':      return <div key={i} className="my-1"><img src={withAuthToken(block.url)} alt={block.caption || ''} className="img-fluid rounded" style={{maxHeight:200}}/></div>;
                      case 'table_row':  return <div key={i} className="d-flex gap-2 border-bottom py-1 small">{(block.cells||[]).map((c,j)=><span key={j} className="flex-fill">{c}</span>)}</div>;
                      default: return null;
                    }
                  })}
                </div>
                <div className="alert alert-success py-1 px-2 small mb-0 mt-2">
                  <i className="bi bi-info-circle me-1"/>
                  Looks good? Click <strong>Upload</strong> below to save it to the selected topic.
                </div>
              </div>
            )}
          </div>
        )}
        {type === 'html' && (
          <div className="col-12">
            <label className="form-label">HTML Content <span className="text-danger">*</span></label>
            <textarea className="form-control form-control-sm" rows={6} value={html} onChange={e => setHtml(e.target.value)} placeholder="<p>Your content here...</p>"/>
          </div>
        )}
        {!['link','internal_link','html','notion'].includes(type) && (
          <div className="col-12">
            <label className="form-label">File <span className="text-danger">*</span></label>
            <input className="form-control form-control-sm" type="file" onChange={e => { setFile(e.target.files?.[0] || null); setErr(''); }}
              accept=".pdf,.mp4,.webm,.mov,.avi,.mkv,.mpeg,.mpg,.doc,.docx,.ppt,.pptx"/>
            {type === 'video' && (
              <div className={`form-text ${file ? 'text-success' : 'text-danger'}`}>
                <i className={`bi ${file ? 'bi-check-circle-fill' : 'bi-exclamation-circle'} me-1`} />
                {file ? `Video ready to upload: ${file.name} (${fmtSize(file.size)})` : 'Video not selected.'}
                {' '}{lmsThumbnailUrl ? 'Thumbnail uploaded.' : 'Thumbnail not uploaded.'}
              </div>
            )}
          </div>
        )}
        </>}
      </div>
    </BSModal>
  );
};

// ─── Structure Manager (Modules + Topics) ─────────────────────────────────────
const StructureManager = ({ courseId, canWrite, canAdmin, onModuleSelect, selectedModuleId, selectedTopicId, onTopicSelect, courses, onAlert }) => {
  const [modules, setModules] = useState([]);
  const [expandedMod, setExpandedMod] = useState(null);
  const [expandedMods, setExpandedMods] = useState(new Set()); // Set of expanded module IDs — supports multiple open
  const [topicsByMod, setTopicsByMod] = useState({});
  const [newModTitle, setNewModTitle] = useState('');
  const [newTopTitles, setNewTopTitles] = useState({});
  const [editMod, setEditMod] = useState(null);
  const [editTop, setEditTop] = useState(null);
  const [cloneModal, setCloneModal] = useState(null); // { type:'topic', sourceTopicId, sourceModuleId } or { type:'course' }
  const [cloneTarget, setCloneTarget] = useState({ courseId:'', moduleId:'', topicId:'' });
  const [cloneModules, setCloneModules] = useState([]);
  const [cloneTopics, setCloneTopics] = useState([]);
  const [cloning, setCloning] = useState(false);
  // Source preview for course reuse — shows source modules/topics with their orders so user can reorder before cloning
  const [sourcePreview, setSourcePreview] = useState({ modules: [], topicsByMod: {}, loading: false });
  // Track custom order for source modules — user can reorder before cloning
  const [sourceModuleOrder, setSourceModuleOrder] = useState([]); // array of module _ids in desired order

  // ── Drag-and-drop reorder ─────────────────────────────────────────────────
  const [draggedModId, setDraggedModId] = useState(null);
  const [dragOverModId, setDragOverModId] = useState(null);
  const [draggedTop, setDraggedTop] = useState({ topId: null, modId: null });
  const [dragOverTopId, setDragOverTopId] = useState(null);
  // Undo stack — tracks the last move/nest action so users can revert
  const [lastMove, setLastMove] = useState(null); // { kind: 'topic'|'module', id, prev: { moduleId/parentId, order } }
  // Alert state — declared here so helper functions above can reference setAlert
  const [alert, setAlert] = useState({ show: false, msg: '', type: 'success' });

  const handleModuleDrop = async (targetModId) => {
    if (!draggedModId || draggedModId === targetModId) return;
    const sourceMod = modules.find(m => m._id === draggedModId);
    const targetMod = modules.find(m => m._id === targetModId);
    if (!sourceMod || !targetMod) return;
    const nextParentId = targetMod.parentId || null;
    const siblingModules = modules
      .filter(m => (m.parentId || null) === nextParentId && m._id !== draggedModId)
      .sort((a, b) => a.order - b.order);
    const targetIndex = siblingModules.findIndex(m => m._id === targetModId);
    if (targetIndex < 0) return;
    const reorderedSiblings = [...siblingModules];
    reorderedSiblings.splice(targetIndex, 0, { ...sourceMod, parentId: nextParentId });
    setLastMove({ kind: 'module', id: draggedModId, prev: { parentId: sourceMod.parentId || null, order: sourceMod.order } });
    setModules(prev => prev.map(m => {
      if ((m.parentId || null) !== nextParentId && m._id !== draggedModId) return m;
      if (m._id === draggedModId) {
        const movedIndex = reorderedSiblings.findIndex(x => x._id === m._id);
        return { ...m, parentId: nextParentId, order: movedIndex >= 0 ? movedIndex : m.order };
      }
      const siblingIndex = reorderedSiblings.findIndex(x => x._id === m._id);
      return siblingIndex >= 0 ? { ...m, order: siblingIndex } : m;
    }));
    setDraggedModId(null); setDragOverModId(null);
    try {
      await api.patch(`/training/course-content/modules/${draggedModId}/move`, {
        parentId: nextParentId,
        order: targetIndex,
      });
      fetchModules();
    } catch(_) { fetchModules(); }
  };

  const handleTopicDrop = async (targetTopId, modId) => {
    const srcTopId = draggedTop.topId;
    if (!srcTopId || srcTopId === targetTopId) return;
    const topics = topicsByMod[modId] || [];
    const from = topics.findIndex(t => t._id === srcTopId);
    const to   = topics.findIndex(t => t._id === targetTopId);
    if (from < 0 || to < 0) return;
    const reordered = [...topics];
    reordered.splice(to, 0, reordered.splice(from, 1)[0]);
    setTopicsByMod(p => ({ ...p, [modId]: reordered }));
    setDraggedTop({ topId: null, modId: null }); setDragOverTopId(null);
    try {
      await api.patch('/training/course-content/topics/reorder', {
        topics: reordered.map((t, i) => ({ _id: t._id, order: i })),
      });
    } catch(_) { fetchTopics(modId); }
  };

  // Move a topic to a different module (drop on module header)
  const handleTopicDropOnModule = async (targetModId) => {
    const { topId: srcTopId, modId: srcModId } = draggedTop;
    if (!srcTopId || !srcModId || srcModId === targetModId) return;

    // Optimistic update — remove from old, add to end of new
    const srcTopics = (topicsByMod[srcModId] || []).filter(t => t._id !== srcTopId);
    const dstTopics = topicsByMod[targetModId] || [];
    const movedTopic = (topicsByMod[srcModId] || []).find(t => t._id === srcTopId);
    if (!movedTopic) return;
    const newOrder = dstTopics.length;

    setTopicsByMod(p => ({
      ...p,
      [srcModId]: srcTopics,
      [targetModId]: [...dstTopics, { ...movedTopic, moduleId: targetModId }],
    }));
    setDraggedTop({ topId: null, modId: null }); setDragOverTopId(null); setDragOverModId(null);

    // Save undo state BEFORE the move
    setLastMove({ kind: 'topic', id: srcTopId, prev: { moduleId: srcModId, order: movedTopic.order } });

    try {
      await api.patch(`/training/course-content/topics/${srcTopId}/move`, {
        moduleId: targetModId,
        order: newOrder,
      });
    } catch(_) {
      fetchTopics(srcModId);
      fetchTopics(targetModId);
    }
  };

  // Move a module to be a child of another module (nesting) by dropping on the target's header
  const handleModuleDropOnModule = async (targetModId) => {
    if (!draggedModId || draggedModId === targetModId) return;
    const targetMod = modules.find(m => m._id === targetModId);
    const sourceMod = modules.find(m => m._id === draggedModId);
    if (!targetMod || !sourceMod) return;
    // Prevent self-descendant nesting (simple client-side check — server enforces too)
    if (targetMod.parentId === sourceMod._id) return;
    // Save undo state BEFORE the move
    setLastMove({ kind: 'module', id: draggedModId, prev: { parentId: sourceMod.parentId || null, order: sourceMod.order } });
    // Optimistic update
    setModules(prev => prev.map(m => m._id === draggedModId ? { ...m, parentId: targetModId } : m));
    setDraggedModId(null); setDragOverModId(null);
    try {
      await api.patch(`/training/course-content/modules/${draggedModId}/move`, {
        parentId: targetModId,
        order: 0,
      });
    } catch(_) { fetchModules(); }
  };

  const moveModuleByStep = async (moduleId, direction) => {
    const module = modules.find(m => m._id === moduleId);
    if (!module) return;
    const parentId = module.parentId || null;
    const siblings = modules
      .filter(m => (m.parentId || null) === parentId)
      .sort((a, b) => a.order - b.order);
    const currentIndex = siblings.findIndex(m => m._id === moduleId);
    if (currentIndex < 0) return;
    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= siblings.length) return;

    const reordered = [...siblings];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    setLastMove({ kind: 'module', id: moduleId, prev: { parentId, order: module.order } });
    setModules(prev => prev.map(m => {
      if ((m.parentId || null) !== parentId) return m;
      const idx = reordered.findIndex(x => x._id === m._id);
      return idx >= 0 ? { ...m, order: idx } : m;
    }));

    try {
      await api.patch(`/training/course-content/modules/${moduleId}/move`, {
        parentId,
        order: targetIndex,
      });
      fetchModules();
    } catch (_) {
      fetchModules();
    }
  };

  // Undo the last drag/drop move — uses setAlert directly to avoid hoisting issues with showAlert
  const handleUndo = async () => {
    if (!lastMove) return;
    const { kind, id, prev } = lastMove;
    setLastMove(null);
    try {
      if (kind === 'topic') {
        await api.patch(`/training/course-content/topics/${id}/move`, {
          moduleId: prev.moduleId,
          order: prev.order,
        });
        fetchTopics(prev.moduleId);
        const tgtModId = Object.entries(topicsByMod).find(([mid, arr]) => arr?.some(t => t._id === id))?.[0];
        if (tgtModId && tgtModId !== prev.moduleId) fetchTopics(tgtModId);
      } else if (kind === 'module') {
        await api.patch(`/training/course-content/modules/${id}/move`, {
          parentId: prev.parentId || null,
          order: prev.order,
        });
        fetchModules();
      }
      setAlert({ show: true, msg: 'Move undone.', type: 'success' });
    } catch (e) {
      setAlert({ show: true, msg: e.response?.data?.message || 'Undo failed.', type: 'danger' });
    }
  };

  // Fetch modules/topics for clone target course
  const onCloneCourseChange = async (cid) => {
    setCloneTarget(p => ({ ...p, courseId: cid, moduleId:'', topicId:'' }));
    setCloneModules([]); setCloneTopics([]);
    if (!cid) return;
    try {
      const r = await api.get(`/training/course-content/modules/${cid}`);
      setCloneModules(r.data?.records || r.data?.data || []);
    } catch(_) {}
  };
  const onCloneModChange = async (mid) => {
    setCloneTarget(p => ({ ...p, moduleId: mid, topicId:'' }));
    setCloneTopics([]);
    if (!mid) return;
    try {
      const r = await api.get(`/training/course-content/topics/${mid}`);
      setCloneTopics(r.data?.records || r.data?.data || []);
    } catch(_) {}
  };

  const handleClone = async () => {
    setCloning(true);
    try {
if (cloneModal.type === 'topic') {
        const res = await api.post('/training/course-content/clone-topic', {
          sourceTopicId: cloneModal.sourceTopicId,
          targetCourseId: cloneTarget.courseId,
          // Only pass target module/topic if NOT creating new
          targetModuleId: cloneTarget.createNew ? undefined : cloneTarget.moduleId,
          targetTopicId:  cloneTarget.createNew ? undefined : cloneTarget.topicId,
          newModuleName: cloneTarget.createNew ? (cloneTarget.newModuleName || undefined) : undefined,
          newTopicName:  cloneTarget.createNew ? (cloneTarget.newTopicName || undefined) : undefined,
        });
        const data = res.data?.data || {};
        if (data.createdModule || data.createdTopic) {
          onAlert?.(`Topic content cloned. Created new module + topic in target course (${data.clonedCount} item(s)).`, 'success');
        } else {
          onAlert?.(`Topic content cloned (${data.clonedCount} item(s)).`, 'success');
        }
      } else {
        if (cloneTarget.wrapInParent && !cloneTarget.wrapInParentName?.trim()) {
          onAlert?.('Please enter a parent module name.', 'warning');
          return;
        }
        const res = await api.post('/training/course-content/clone-course', {
          sourceCourseId: courseId,
          targetCourseId: cloneTarget.courseId,
          sourceModuleOrder: sourceModuleOrder.length > 0 ? sourceModuleOrder : undefined,
          replaceExisting: !!cloneTarget.replaceExisting,
          wrapInParent: !!cloneTarget.wrapInParent,
          wrapInParentName: cloneTarget.wrapInParent ? cloneTarget.wrapInParentName.trim() : undefined,
        });
        const data = res.data?.data || {};
        const tgt = data.targetCourseName || cloneTarget.courseId;
        const src = data.sourceCourseName || '';
        let msg;
        if (data.deletedModules > 0) {
          msg = `Replaced ${data.deletedModules} module(s). Cloned ${data.clonedModules} new module(s) from "${src}" into "${tgt}".`;
        } else if (data.parentModuleTitle) {
          msg = `Cloned ${data.clonedModules} module(s) from "${src}" wrapped under "${data.parentModuleTitle}" in "${tgt}".`;
        } else {
          msg = `Cloned ${data.clonedModules} module(s) from "${src}" into "${tgt}".`;
        }
        onAlert?.(msg, 'success');
      }
      setCloneModal(null);
      setCloneTarget({ courseId:'', moduleId:'', topicId:'' });
    } catch(e) { onAlert?.(e.response?.data?.message || 'Clone failed.', 'danger'); }
    setCloning(false);
  };

  const fetchModules = useCallback(async () => {
    if (!courseId) return;
    try {
      const r = await api.get(`/training/course-content/modules/${courseId}`);
      setModules(r.data?.records || r.data?.data || []);
    } catch(_) {}
  }, [courseId]);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const fetchTopics = async (modId) => {
    try {
      const r = await api.get(`/training/course-content/topics/${modId}`);
      setTopicsByMod(p => ({ ...p, [modId]: r.data?.records || r.data?.data || [] }));
    } catch(_) {}
  };

  const toggleMod = (modId) => {
    console.log('[toggleMod] called with modId:', modId, 'current expanded:', [...expandedMods]);
    setExpandedMods(prev => {
      const next = new Set(prev);
      if (next.has(modId)) {
        // Collapse this module and all descendants
        const collapseAll = (id) => {
          next.delete(id);
          modules.filter(m => (m.parentId || null) === id).forEach(c => collapseAll(c._id));
        };
        collapseAll(modId);
      } else {
        // Expand this module and all its ancestors (so it's visible)
        next.add(modId);
        let cur = modules.find(x => x._id === modId);
        while (cur && cur.parentId) {
          next.add(cur.parentId);
          cur = modules.find(x => x._id === cur.parentId);
        }
      }
      return next;
    });
    // Fetch topics for this module and its ancestors
    if (!topicsByMod[modId]) fetchTopics(modId);
  };

  const addModule = async () => {
    if (!newModTitle.trim()) return;
    try {
      await api.post('/training/course-content/modules', { courseId, title: newModTitle.trim() });
      setNewModTitle(''); fetchModules();
    } catch(_) {}
  };

  const addTopic = async (modId) => {
    const title = newTopTitles[modId] || '';
    if (!title.trim()) return;
    try {
      await api.post('/training/course-content/topics', { courseId, moduleId: modId, title: title.trim() });
      setNewTopTitles(p => ({ ...p, [modId]: '' }));
      fetchTopics(modId);
    } catch(_) {}
  };

  const saveMod = async () => {
    try { await api.put(`/training/course-content/modules/${editMod._id}`, { title: editMod.title, description: editMod.description }); setEditMod(null); fetchModules(); } catch(_) {}
  };

  const saveTop = async () => {
    try { await api.put(`/training/course-content/topics/${editTop._id}`, { title: editTop.title, description: editTop.description }); setEditTop(null); fetchTopics(editTop.moduleId); } catch(_) {}
  };

  const delMod = async (m) => {
    if (!canAdmin && !canWrite) {
      onAlert?.('You do not have permission to delete modules.', 'danger');
      return;
    }
    // Fetch topic count for the module first to inform user
    let topicCount = 0;
    try {
      const tRes = await api.get(`/training/course-content/topics/${m._id}`);
      topicCount = (tRes.data?.records || tRes.data?.data || []).length;
    } catch(_) {}
    let confirmMsg = `Delete module "${m.title}"?`;
    if (topicCount > 0) {
      confirmMsg = `Module "${m.title}" has ${topicCount} topic(s). Delete the module AND all its topics and content?`;
    }
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.delete(`/training/course-content/modules/${m._id}`, { params: { force: topicCount > 0 ? 'true' : undefined } });
      onAlert?.(topicCount > 0 ? `Module and ${topicCount} topic(s) deleted.` : 'Module deleted.', 'success');
      fetchModules();
    } catch (e) {
      onAlert?.(e.response?.data?.message || 'Delete failed.', 'danger');
    }
  };

  const delTop = async (t) => {
    if (!canAdmin && !canWrite) {
      onAlert?.('You do not have permission to delete topics.', 'danger');
      return;
    }
    // Check if topic has content items
    let itemCount = 0;
    try {
      const iRes = await api.get(`/training/course-content/topic-items/${t._id}`);
      const items = iRes.data?.data?.items ?? iRes.data?.records ?? iRes.data?.data;
      if (!Array.isArray(items)) throw new Error('Unable to verify topic content. Please refresh and try again.');
      itemCount = items.length;
    } catch (e) {
      onAlert?.(e.response?.data?.message || e.message || 'Unable to verify topic content. Please try again.', 'danger');
      return;
    }
    let confirmMsg = `Delete topic "${t.title}"?`;
    if (itemCount > 0) {
      confirmMsg = `Topic "${t.title}" has ${itemCount} content item(s). Delete the topic AND all its content?`;
    }
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.delete(`/training/course-content/topics/${t._id}`, { params: { force: itemCount > 0 ? 'true' : undefined } });
      onAlert?.(itemCount > 0 ? `Topic and ${itemCount} content item(s) deleted.` : 'Topic deleted.', 'success');
      fetchTopics(t.moduleId);
    } catch (e) {
      onAlert?.(e.response?.data?.message || 'Delete failed.', 'danger');
    }
  };

  if (!courseId) return <div className="text-muted text-center py-3">Select a course to manage content.</div>;

// Recursive module renderer — supports nested modules via parentId
  const renderModuleTree = (parentId = null, depth = 0) => {
    const children = modules
      .filter(m => (m.parentId || null) === parentId)
      .sort((a, b) => a.order - b.order);
    console.log('[renderModuleTree] parentId:', parentId, 'depth:', depth, 'children count:', children.length, 'moduleIds:', children.map(c => c._id));

    const depthColors = ['', 'border-start border-3 border-info', 'border-start border-3 border-success', 'border-start border-3 border-warning', 'border-start border-3 border-danger'];
    const depthBg      = ['bg-white', 'bg-light bg-opacity-25', 'bg-info bg-opacity-10', 'bg-warning bg-opacity-10', 'bg-danger bg-opacity-10'];
    const depthIconColor = ['#0d6efd', '#0dcaf0', '#198754', '#fd7e14', '#dc3545'];

    return children.map(m => {
      const isExpanded = expandedMods.has(m._id);
      const isSelected = selectedModuleId === m._id;
      const topicCount = (topicsByMod[m._id] || []).length;
      const childCount = modules.filter(x => (x.parentId || null) === m._id).length;
      const siblingModules = modules
        .filter(x => (x.parentId || null) === (m.parentId || null))
        .sort((a, b) => a.order - b.order);
      const siblingIndex = siblingModules.findIndex(x => x._id === m._id);
      const canMoveUp = siblingIndex > 0;
      const canMoveDown = siblingIndex >= 0 && siblingIndex < siblingModules.length - 1;
      if (m.parentId) {
        console.log('[renderModuleTree item] nested module:', m._id, m.title, 'parentId:', m.parentId, 'depth:', depth, 'isExpanded:', isExpanded, 'expandedMod:', expandedMod, 'hasChildren:', childCount > 0);
      }

      return (
        <div key={m._id}
          className={`mb-2 rounded shadow-sm border module-row ${isSelected ? 'border-primary border-2' : 'border-secondary border-opacity-25'} ${dragOverModId === m._id && draggedModId !== m._id ? 'border-primary border-2' : ''} ${depthBg[depth] || ''} ${depth > 0 ? depthColors[depth] || '' : ''}`}
          data-module-id={m._id}
          style={{ marginLeft: depth > 0 ? 8 : 0, transition: 'all 0.15s' }}
          onDragOver={canWrite ? e => {
            e.preventDefault();
            e.stopPropagation();
            setDragOverModId(m._id);
          } : undefined}
          onDrop={canWrite ? e => {
            e.preventDefault();
            e.stopPropagation();
            if (draggedModId && draggedModId !== m._id) {
              handleModuleDrop(m._id);
            } else if (draggedTop.topId && draggedTop.modId !== m._id) {
              handleTopicDropOnModule(m._id);
            }
          } : undefined}
        >
          {/* Module header — large clickable area (separate from drag) */}
          <div
            className={`d-flex align-items-center gap-1 px-2 py-1 rounded-top module-header ${isSelected ? 'bg-primary text-white' : 'bg-white'}`}
            style={{ cursor: 'pointer', minHeight: 38, userSelect: 'none', gap: '4px' }}
            onClick={(e) => {
              console.log('[module-header onClick] moduleId:', m._id, 'title:', m.title, 'parentId:', m.parentId, 'depth:', depth, 'isExpanded:', isExpanded);
              toggleMod(m._id);
              onModuleSelect(m._id);
            }}
            title={`${m.title}${m.description ? ' — ' + m.description : ''}\n${topicCount} topic(s), ${childCount} submodule(s)\nClick to ${isExpanded ? 'collapse' : 'expand'}`}
          >
            {/* Drag handle — smaller, compact */}
            {canWrite ? (
              <span
                draggable
                onDragStart={e => { e.stopPropagation(); e.dataTransfer.setData('text/plain', m._id); setDraggedModId(m._id); }}
                onDragEnd={() => { setDraggedModId(null); setDragOverModId(null); }}
                style={{ cursor: 'grab', padding: '0 2px', display: 'inline-flex', flexShrink: 0 }}
                title="Drag to reorder or nest"
                onClick={e => e.stopPropagation()}>
                <i className="bi bi-grip-vertical text-secondary" style={{ fontSize: '0.85rem', opacity: 0.5 }}/>
              </span>
            ) : null}
            {/* Expand/collapse chevron */}
            <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} ${isSelected ? 'text-white' : 'text-secondary'}`}
              style={{ fontSize: '0.85rem', cursor: 'pointer', flexShrink: 0 }}/>
            {/* Module icon */}
            <i className={`bi bi-${depth > 0 ? 'folder2-open' : 'collection'} ${isSelected ? 'text-white' : ''}`}
              style={{ color: isSelected ? '#fff' : depthIconColor[depth] || '#0d6efd', fontSize: '0.95rem', flexShrink: 0 }}/>
            {/* Title — takes most space, allows full text display */}
            <div className="flex-grow-1" style={{ minWidth: 0, overflow: 'hidden' }}>
              <span className={`fw-semibold ${depth === 0 ? 'fs-6' : 'small'}`}
                style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {m.title}
              </span>
            </div>
            {/* Counts — compact inline */}
            <span className={`badge bg-light text-secondary ${isSelected ? 'opacity-75' : ''}`} style={{ fontSize: '0.65rem', flexShrink: 0, padding: '2px 5px' }}>
              {topicCount > 0 && <><i className="bi bi-file-text me-1"/>{topicCount}</>}
              {childCount > 0 && <span className={topicCount > 0 ? 'ms-2' : ''}><i className="bi bi-folder2 me-1"/>{childCount}</span>}
            </span>
            {/* Action buttons — compact icon-only */}
            {canWrite && (
              <div className="d-flex gap-0" style={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                {m.parentId && (
                  <button className="btn btn-outline-warning module-action-btn" style={{ padding: '1px 4px', fontSize: '0.7rem', lineHeight: 1 }}
                    onClick={async () => {
                      setModules(prev => prev.map(x => x._id === m._id ? { ...x, parentId: null } : x));
                      try { await api.patch(`/training/course-content/modules/${m._id}/move`, { parentId: null, order: 0 }); }
                      catch(_) { fetchModules(); }
                    }} title="Move to top level">
                    <i className="bi bi-arrow-up"/>
                  </button>
                )}
                <button className={`btn ${isSelected ? 'btn-outline-light' : 'btn-outline-secondary'} module-action-btn`}
                  style={{ padding: '1px 4px', fontSize: '0.7rem', lineHeight: 1 }}
                  onClick={() => setEditMod({...m})} title="Edit module">
                  <i className="bi bi-pencil"/>
                </button>
                <button
                  className={`btn ${isSelected ? 'btn-outline-light' : 'btn-outline-secondary'} module-action-btn`}
                  style={{ padding: '1px 4px', fontSize: '0.7rem', lineHeight: 1 }}
                  onClick={() => moveModuleByStep(m._id, -1)}
                  disabled={!canMoveUp}
                  title="Move up in this level">
                  <i className="bi bi-arrow-up"/>
                </button>
                <button
                  className={`btn ${isSelected ? 'btn-outline-light' : 'btn-outline-secondary'} module-action-btn`}
                  style={{ padding: '1px 4px', fontSize: '0.7rem', lineHeight: 1 }}
                  onClick={() => moveModuleByStep(m._id, 1)}
                  disabled={!canMoveDown}
                  title="Move down in this level">
                  <i className="bi bi-arrow-down"/>
                </button>
                <button
                  className={`btn ${isSelected ? 'btn-outline-light' : 'btn-outline-primary'} module-action-btn`}
                  style={{ padding: '1px 4px', fontSize: '0.7rem', lineHeight: 1 }}
                  onDragOver={e => {
                    if (!canWrite) return;
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={e => {
                    if (!canWrite) return;
                    e.preventDefault();
                    e.stopPropagation();
                    handleModuleDropOnModule(m._id);
                  }}
                  title="Drop a module here to nest under this module">
                  <i className="bi bi-diagram-3"/>
                </button>
                <button className={`btn ${isSelected ? 'btn-outline-light' : 'btn-outline-danger'} module-action-btn`}
                  style={{ padding: '1px 4px', fontSize: '0.7rem', lineHeight: 1 }}
                  onClick={() => delMod(m)} title="Delete module">
                  <i className="bi bi-trash"/>
                </button>
              </div>
            )}
          </div>

          {/* Expanded content */}
          {isExpanded && (
            <div className="p-2" style={{ background: 'rgba(248,249,250,0.5)' }}>
              {/* Topics */}
              {(topicsByMod[m._id] || []).map((t, ti) => {
                const isTopicSelected = selectedTopicId === t._id;
                return (
                  <div key={t._id}
                    className={`d-flex align-items-center px-2 py-1 mb-1 rounded topic-row ${isTopicSelected ? 'bg-primary text-white shadow-sm' : 'bg-white border'}`}
                    style={{ marginLeft: 24, transition: 'all 0.15s', cursor: 'pointer', gap: '4px' }}
                    onDragOver={canWrite ? e => { e.preventDefault(); e.stopPropagation(); setDragOverTopId(t._id); } : undefined}
                    onDrop={canWrite ? e => { e.preventDefault(); e.stopPropagation(); handleTopicDrop(t._id, m._id); } : undefined}
                    onClick={(e) => {
                      console.log('[topic onClick] topicId:', t._id, 'title:', t.title, 'modId:', m._id, 'target:', e.target.tagName, e.target.className);
                      if (e.target.closest('.topic-action-btn') || e.target.closest('.topic-drag-handle')) return;
                      onTopicSelect(t._id, m._id);
                    }}
                    title={`Topic ${ti + 1}: ${t.title}${t.description ? '\n' + t.description : ''}`}>
                    {canWrite && (
                      <span
                        className="topic-drag-handle"
                        draggable
                        onDragStart={e => { e.stopPropagation(); e.dataTransfer.setData('text/plain', t._id); setDraggedTop({ topId: t._id, modId: m._id }); }}
                        onDragEnd={() => { setDraggedTop({ topId: null, modId: null }); setDragOverTopId(null); }}
                        style={{ cursor: 'grab', padding: '0 2px', display: 'inline-flex', flexShrink: 0 }}
                        onClick={e => e.stopPropagation()}
                        title="Drag to reorder or move">
                        <i className={`bi bi-grip-vertical ${isTopicSelected ? 'text-white' : 'text-secondary'}`}
                          style={{ fontSize: '0.8rem', opacity: 0.5 }}/>
                      </span>
                    )}
                    <i className={`bi bi-file-earmark-text ${isTopicSelected ? 'text-white' : 'text-info'}`}
                      style={{ fontSize: '0.85rem', flexShrink: 0 }}/>
                    <span className="flex-grow-1" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                      <span className="text-muted me-1" style={{ fontSize: '0.7rem' }}>{ti + 1}.</span>
                      <span className={isTopicSelected ? '' : 'small'}>{t.title}</span>
                    </span>
                    {canWrite && (
                      <div className="d-flex gap-0" style={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                        <button className={`btn topic-action-btn ${isTopicSelected ? 'btn-outline-light' : 'btn-outline-info'}`}
                          style={{ padding: '1px 4px', fontSize: '0.7rem', lineHeight: 1 }}
                          onClick={() => { setCloneModal({ type:'topic', sourceTopicId: t._id, sourceModuleId: m._id }); setCloneTarget({ courseId:'', moduleId:'', topicId:'' }); }}
                          title="Reuse / Clone">
                          <i className="bi bi-copy"/>
                        </button>
                        <button className={`btn topic-action-btn ${isTopicSelected ? 'btn-outline-light' : 'btn-outline-secondary'}`}
                          style={{ padding: '1px 4px', fontSize: '0.7rem', lineHeight: 1 }}
                          onClick={() => setEditTop({...t, moduleId: m._id})} title="Edit topic">
                          <i className="bi bi-pencil"/>
                        </button>
                        <button className={`btn topic-action-btn ${isTopicSelected ? 'btn-outline-light' : 'btn-outline-danger'}`}
                          style={{ padding: '1px 4px', fontSize: '0.7rem', lineHeight: 1 }}
                          onClick={() => delTop({...t, moduleId: m._id})} title="Delete topic">
                          <i className="bi bi-trash"/>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* New topic input */}
              {canWrite && (
                <div className="d-flex gap-1 mt-1" style={{ marginLeft: 24 }} onClick={e => e.stopPropagation()}>
                  <input className="form-control form-control-sm" placeholder="+ New topic..."
                    value={newTopTitles[m._id] || ''} onChange={e => setNewTopTitles(p => ({ ...p, [m._id]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && addTopic(m._id)}/>
                  <button className="btn btn-outline-primary btn-sm" onClick={() => addTopic(m._id)}>
                    <i className="bi bi-plus-lg"/>
                  </button>
                </div>
              )}

              {/* Recursive child modules */}
              <div className="mt-2">
                {renderModuleTree(m._id, depth + 1)}
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div>
      {renderModuleTree(null, 0)}

      {canWrite && (
        <div className="d-flex gap-1 mt-2">
          <input className="form-control form-control-sm" placeholder="New module..." value={newModTitle}
            onChange={e => setNewModTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && addModule()}/>
          <button className="btn btn-primary btn-sm" onClick={addModule}>Add</button>
          {lastMove && (
            <button className="btn btn-outline-warning btn-sm" onClick={handleUndo}
              title={`Undo: ${lastMove.kind === 'topic' ? 'move topic' : 'nest module'}`}>
              <i className="bi bi-arrow-counterclockwise me-1"/>Undo
            </button>
          )}
        </div>
      )}

      {/* Edit Module Modal */}
      <BSModal show={!!editMod} title="Edit Module" onClose={() => setEditMod(null)}
        footer={<><button className="btn btn-secondary btn-sm" onClick={() => setEditMod(null)}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={saveMod}>Save</button></>}>
        {editMod && <>
          <div className="mb-3">
            <label className="form-label">Title</label>
            <input className="form-control form-control-sm" value={editMod.title} onChange={e => setEditMod(p => ({ ...p, title: e.target.value }))}/>
          </div>
          <div>
            <label className="form-label">Notes / Description</label>
            <textarea className="form-control form-control-sm" rows={3} value={editMod.description || ''} onChange={e => setEditMod(p => ({ ...p, description: e.target.value }))}/>
          </div>
        </>}
      </BSModal>

      {/* Edit Topic Modal */}
      <BSModal show={!!editTop} title="Edit Topic" onClose={() => setEditTop(null)}
        footer={<><button className="btn btn-secondary btn-sm" onClick={() => setEditTop(null)}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={saveTop}>Save</button></>}>
        {editTop && <>
          <div className="mb-3">
            <label className="form-label">Title</label>
            <input className="form-control form-control-sm" value={editTop.title} onChange={e => setEditTop(p => ({ ...p, title: e.target.value }))}/>
          </div>
          <div>
            <label className="form-label">Notes / Description</label>
            <textarea className="form-control form-control-sm" rows={3} value={editTop.description || ''} onChange={e => setEditTop(p => ({ ...p, description: e.target.value }))}/>
          </div>
        </>}
      </BSModal>

      {/* Reuse Course Content button */}
      {canAdmin && courseId && modules.length > 0 && (
        <button className="btn btn-outline-info btn-sm w-100 mt-2"
          onClick={async () => {
            setCloneModal({ type:'course' });
            setCloneTarget({ courseId:'', moduleId:'', topicId:'' });
            // Fetch source course structure for preview & reordering
            setSourcePreview({ modules: [], topicsByMod: {}, loading: true });
            try {
              const modRes = await api.get(`/training/course-content/modules/${courseId}`);
              const srcMods = (modRes.data?.records || modRes.data?.data || []).slice().sort((a,b) => a.order - b.order);
              const topicsByMod = {};
              await Promise.all(srcMods.map(async (m) => {
                const tRes = await api.get(`/training/course-content/topics/${m._id}`);
                topicsByMod[m._id] = (tRes.data?.records || tRes.data?.data || []).slice().sort((a,b) => a.order - b.order);
              }));
              setSourcePreview({ modules: srcMods, topicsByMod, loading: false });
              setSourceModuleOrder(srcMods.map(m => m._id));
            } catch (_) {
              setSourcePreview({ modules: [], topicsByMod: {}, loading: false });
            }
          }}>
          <i className="bi bi-copy me-1"/>Reuse Full Course Content
        </button>
      )}

      {/* Clone / Reuse Modal */}
      <BSModal show={!!cloneModal} title={cloneModal?.type === 'course' ? 'Reuse Course Content' : 'Reuse Topic Content'}
        onClose={() => setCloneModal(null)}
        footer={<>
          <button className="btn btn-secondary btn-sm" onClick={() => setCloneModal(null)}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleClone} disabled={cloning ||
            (cloneModal?.type === 'topic'
              ? (!cloneTarget.courseId || (cloneTarget.createNew ? false : (!cloneTarget.moduleId || !cloneTarget.topicId)))
              : !cloneTarget.courseId)}>
            {cloning ? <><span className="spinner-border spinner-border-sm me-1"/>Cloning...</> : <><i className="bi bi-copy me-1"/>Clone</>}
          </button>
        </>}>
        {cloneModal && (
          <div className="row g-3">
            <div className="col-12">
              <div className="alert alert-info py-2 small mb-2">
                <i className="bi bi-info-circle me-1"/>
                {cloneModal.type === 'course'
                  ? 'This will copy all modules, topics, and content items to the target course.'
                  : 'This will copy all content items from this topic. You can either create a new module/topic or add to an existing one.'}
              </div>
            </div>
            <div className="col-12">
              <label className="form-label">Target Course <span className="text-danger">*</span></label>
              <select className="form-select form-select-sm" value={cloneTarget.courseId}
                onChange={e => onCloneCourseChange(e.target.value)}>
                <option value="">Select target course</option>
                {(courses || []).filter(c => cloneModal.type === 'topic' || c._id !== courseId).map(c =>
                  <option key={c._id} value={c._id}>{c.courseName || c.name}</option>
                )}
              </select>
            </div>
            {/* Source course preview & reorder for full-course reuse */}
            {cloneModal.type === 'course' && (
              <>
                {/* Target course's existing modules */}
                <div className="col-12">
                  <label className="form-label">
                    Target Course's Existing Modules
                    {cloneTarget.courseId && (() => {
                      const tgtCourse = courses.find(c => c._id === cloneTarget.courseId);
                      return tgtCourse ? <span className="text-muted ms-2 small">({tgtCourse.courseName || tgtCourse.name})</span> : null;
                    })()}
                  </label>
                  {!cloneTarget.courseId ? (
                    <div className="text-muted small">Select a target course above to see its existing modules.</div>
                  ) : cloneModules.length === 0 ? (
                    <div className="text-muted small"><i className="bi bi-info-circle me-1"/>Target course has no existing modules.</div>
                  ) : (
                    <div className="border rounded p-2" style={{ maxHeight: '180px', overflowY: 'auto', background: '#fffbe6' }}>
                      {cloneModules.slice().sort((a,b) => a.order - b.order).map((m, idx) => (
                        <div key={m._id} className="small py-1 border-bottom">
                          <i className="bi bi-collection me-1 text-warning"/> {idx + 1}. {m.title}
                          {m.description && <span className="text-muted ms-1">— {m.description}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="form-check mt-2">
                    <input className="form-check-input" type="checkbox" id="replaceExisting"
                      checked={cloneTarget.replaceExisting || false}
                      onChange={e => setCloneTarget(p => ({ ...p, replaceExisting: e.target.checked }))}/>
                    <label className="form-check-label small" htmlFor="replaceExisting">
                      Replace existing modules (otherwise new modules will be appended)
                    </label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="wrapInParent"
                      checked={cloneTarget.wrapInParent || false}
                      onChange={e => setCloneTarget(p => ({ ...p, wrapInParent: e.target.checked, wrapInParentName: p.wrapInParentName || (courseId ? (courses.find(c => c._id === courseId)?.courseName || courses.find(c => c._id === courseId)?.name || '') : '') }))}/>
                    <label className="form-check-label small" htmlFor="wrapInParent">
                      <i className="bi bi-folder2 me-1 text-primary"/>
                      Wrap all modules inside a new parent module
                    </label>
                  </div>
                  {cloneTarget.wrapInParent && (
                    <div className="mt-2 ms-4">
                      <label className="form-label small">Parent Module Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control form-control-sm"
                        placeholder="e.g. Core Python, Advanced Concepts, etc."
                        value={cloneTarget.wrapInParentName || ''}
                        onChange={e => setCloneTarget(p => ({ ...p, wrapInParentName: e.target.value }))}/>
                    </div>
                  )}
                </div>
                <div className="col-12">
                  <label className="form-label">
                  Source Structure — from
                  {(() => {
                    const srcCourse = courses.find(c => c._id === courseId);
                    return srcCourse ? <strong className="text-primary ms-1">"{srcCourse.courseName || srcCourse.name}"</strong> : null;
                  })()}
                  <span className="text-muted ms-1">(drag to reorder before cloning)</span>
                </label>
                  {sourcePreview.loading ? (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-primary"/> Loading source structure...
                    </div>
                  ) : sourcePreview.modules.length === 0 ? (
                    <div className="text-muted small">No modules in source course.</div>
                  ) : (
                    <div className="border rounded p-2" style={{ maxHeight: '280px', overflowY: 'auto', background: '#fafbfc' }}>
                      {sourceModuleOrder.map((mid, idx) => {
                        const m = sourcePreview.modules.find(x => x._id === mid);
                        if (!m) return null;
                        const topics = sourcePreview.topicsByMod[mid] || [];
                        return (
                          <div key={mid} className="d-flex align-items-start gap-2 py-1 border-bottom"
                            draggable
                            onDragStart={e => { e.dataTransfer.setData('text/plain', mid); }}
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => {
                              e.preventDefault();
                              const draggedId = e.dataTransfer.getData('text/plain');
                              if (!draggedId || draggedId === mid) return;
                              setSourceModuleOrder(prev => {
                                const next = prev.filter(x => x !== draggedId);
                                const targetIdx = next.indexOf(mid);
                                next.splice(targetIdx, 0, draggedId);
                                return next;
                              });
                            }}
                            style={{ cursor: 'grab' }}>
                            <i className="bi bi-grip-vertical text-muted"/>
                            <div className="flex-grow-1">
                              <div className="small fw-semibold">{idx + 1}. {m.title}</div>
                              {topics.length > 0 && (
                                <div className="text-muted" style={{ fontSize: '0.75rem', marginLeft: '12px' }}>
                                  {topics.map((t, ti) => (
                                    <div key={t._id}>↳ {t.title}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="d-flex flex-column gap-1">
                              <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1"
                                disabled={idx === 0}
                                onClick={() => setSourceModuleOrder(prev => {
                                  if (idx === 0) return prev;
                                  const next = [...prev];
                                  [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                                  return next;
                                })} title="Move up">▲</button>
                              <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1"
                                disabled={idx === sourceModuleOrder.length - 1}
                                onClick={() => setSourceModuleOrder(prev => {
                                  if (idx === sourceModuleOrder.length - 1) return prev;
                                  const next = [...prev];
                                  [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
                                  return next;
                                })} title="Move down">▼</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
            {cloneModal.type === 'topic' && (
              <>
                <div className="col-12">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="createNewForTopic"
                      checked={cloneTarget.createNew || false}
                      onChange={e => setCloneTarget(p => ({ ...p, createNew: e.target.checked, moduleId: '', topicId: '' }))}/>
                    <label className="form-check-label small" htmlFor="createNewForTopic">
                      <i className="bi bi-plus-circle me-1 text-success"/> Create new module & topic in target course
                      <span className="text-muted ms-1">(otherwise clone into existing module/topic)</span>
                    </label>
                  </div>
                </div>
                {cloneTarget.createNew ? (
                  <>
                    <div className="col-12">
                      <label className="form-label">New Module Name <span className="text-muted small">(optional — defaults to source course name)</span></label>
                      <input type="text" className="form-control form-control-sm"
                        placeholder="e.g. Core Python Course Content"
                        value={cloneTarget.newModuleName || ''}
                        onChange={e => setCloneTarget(p => ({ ...p, newModuleName: e.target.value }))}/>
                    </div>
                    <div className="col-12">
                      <label className="form-label">New Topic Name <span className="text-muted small">(optional — defaults to source topic name)</span></label>
                      <input type="text" className="form-control form-control-sm"
                        placeholder="e.g. Introduction to Python"
                        value={cloneTarget.newTopicName || ''}
                        onChange={e => setCloneTarget(p => ({ ...p, newTopicName: e.target.value }))}/>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-12">
                      <label className="form-label">Target Module <span className="text-danger">*</span></label>
                      <select className="form-select form-select-sm" value={cloneTarget.moduleId}
                        onChange={e => onCloneModChange(e.target.value)} disabled={!cloneTarget.courseId}>
                        <option value="">Select target module</option>
                        {cloneModules.map(m => <option key={m._id} value={m._id}>{m.title}</option>)}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Target Topic <span className="text-danger">*</span></label>
                      <select className="form-select form-select-sm" value={cloneTarget.topicId}
                        onChange={e => setCloneTarget(p => ({ ...p, topicId: e.target.value }))} disabled={!cloneTarget.moduleId}>
                        <option value="">Select target topic</option>
                        {cloneTopics.map(t => <option key={t._id} value={t._id}>{t.title}</option>)}
                      </select>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </BSModal>
    </div>
  );
};

// ─── Edit Content Modal ───────────────────────────────────────────────────────
const EditContentModal = ({ item, onClose, onSaved }) => {
  const isPrivateLink = item?.type === 'internal_link' && item?.externalUrl?.startsWith('private://');
  const isHtml        = item?.type === 'html';
  const isNotion       = item?.type === 'notion';
  const isLinkType     = ['link', 'internal_link'].includes(item?.type) && !isPrivateLink;
  const showVideoLessonFields = item?.type === 'video';
  const showQuizLessonFields = item?.type === 'quiz';

  const [title, setTitle]       = useState(item?.title || '');
  const [desc, setDesc]         = useState(item?.description || '');
  const [extUrl, setExtUrl]     = useState(item?.externalUrl || '');
  const [editedHtml, setEditedHtml] = useState(item?.htmlContent || '');
  const [privateHtml, setPrivateHtml] = useState('');
  const [fetchingHtml, setFetchingHtml] = useState(false);
  const [isLmsLesson, setIsLmsLesson] = useState(!!item?.lmsLesson?.enabled);
  const [lmsThumbnailUrl, setLmsThumbnailUrl] = useState(item?.lmsLesson?.thumbnailUrl || '');
  const [lmsDurationSeconds, setLmsDurationSeconds] = useState(item?.lmsLesson?.durationSeconds != null ? String(item.lmsLesson.durationSeconds) : '');
  const [lmsAvailableFromPlan, setLmsAvailableFromPlan] = useState(item?.lmsLesson?.availableFromPlan || '');
  const [lmsRequiresItemIds, setLmsRequiresItemIds] = useState(Array.isArray(item?.lmsLesson?.requiresItemIds) ? item.lmsLesson.requiresItemIds.map(String) : []);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailDimensions, setThumbnailDimensions] = useState(null);
  const [replacementVideo, setReplacementVideo] = useState(null);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [prerequisiteOptions, setPrerequisiteOptions] = useState([]);
  const [loadingPrerequisites, setLoadingPrerequisites] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [quizzesLoading, setQuizzesLoading] = useState(false);
  const [lmsQuizId, setLmsQuizId] = useState(String(item?.lmsLesson?.quizRef?._id || item?.lmsLesson?.quizRef || ''));
  const [lmsQuizRequirement, setLmsQuizRequirement] = useState(item?.lmsLesson?.quizRequirement || 'optional');
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  useEffect(() => {
    if (!showQuizLessonFields) return;
    let cancelled = false;
    setQuizzesLoading(true);
    api.get('/quiz', { params: { limit: 500 } })
      .then((response) => {
        if (!cancelled) setQuizzes(response.data?.records || response.data?.data?.records || response.data?.data || []);
      })
      .catch(() => { if (!cancelled) setQuizzes([]); })
      .finally(() => { if (!cancelled) setQuizzesLoading(false); });
    return () => { cancelled = true; };
  }, [showQuizLessonFields]);

  // For notion items: convert blocks → HTML for WYSIWYG editing
  const notionToHtml = (blocks) => {
    if (!blocks || !blocks.length) return '';
    return blocks.map(b => {
      const t = (b.text || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      switch (b.type) {
        case 'heading_1': return `<h1>${t}</h1>`;
        case 'heading_2': return `<h2>${t}</h2>`;
        case 'heading_3': return `<h3>${t}</h3>`;
        case 'paragraph': return `<p>${t}</p>`;
        case 'bullet':    return `<li>${t}</li>`;
        case 'numbered':  return `<li>${t}</li>`;
        case 'code':      return `<pre><code>${t}</code></pre>`;
        case 'quote':     return `<blockquote>${t}</blockquote>`;
        case 'callout':  return `<div class="callout">${t}</div>`;
        case 'divider':  return `<hr/>`;
        case 'todo':     return `<div class="todo"><input type="checkbox" ${b.checked ? 'checked' : ''} disabled/> ${t}</div>`;
        case 'toggle':   return `<details><summary>${t}</summary></details>`;
        case 'image':    return `<img src="${b.url || ''}" alt="${b.caption || ''}"/>`;
        case 'table_row': return `<div class="tablerow">${(b.cells || []).map(c => `<span>${c}</span>`).join('')}</div>`;
        default:          return `<p>${t}</p>`;
      }
    }).join('\n');
  };

  // For notion items: convert edited HTML → blocks on save
  const htmlToNotionBlocks = (html) => {
    if (!html) return [];
    const blocks = [];
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.body.childNodes.forEach(node => {
      if (node.nodeType === 3) return; // skip text nodes
      if (node.tagName === 'H1') blocks.push({ type: 'heading_1', text: node.textContent || '' });
      else if (node.tagName === 'H2') blocks.push({ type: 'heading_2', text: node.textContent || '' });
      else if (node.tagName === 'H3') blocks.push({ type: 'heading_3', text: node.textContent || '' });
      else if (node.tagName === 'P') blocks.push({ type: 'paragraph', text: node.textContent || '' });
      else if (node.tagName === 'LI') blocks.push({ type: node.parentElement?.tagName === 'OL' ? 'numbered' : 'bullet', text: node.textContent || '' });
      else if (node.tagName === 'PRE') blocks.push({ type: 'code', text: node.textContent || '' });
      else if (node.tagName === 'BLOCKQUOTE') blocks.push({ type: 'quote', text: node.textContent || '' });
      else if (node.tagName === 'DIV' && node.className === 'callout') blocks.push({ type: 'callout', text: node.textContent || '' });
      else if (node.tagName === 'DIV' && node.className === 'todo') {
        const cb = node.querySelector('input[type="checkbox"]');
        blocks.push({ type: 'todo', text: node.textContent || '', checked: cb?.checked || false });
      }
      else if (node.tagName === 'DETAILS') blocks.push({ type: 'toggle', text: node.querySelector('summary')?.textContent || '' });
      else if (node.tagName === 'HR') blocks.push({ type: 'divider', text: '' });
      else if (node.tagName === 'IMG') blocks.push({ type: 'image', url: node.src, caption: node.alt || '' });
      else if (node.tagName === 'DIV' && node.className === 'tablerow') {
        const cells = Array.from(node.querySelectorAll('span')).map(s => s.textContent || '');
        blocks.push({ type: 'table_row', cells });
      }
      else if (node.tagName === 'DIV') blocks.push({ type: 'paragraph', text: node.textContent || '' });
    });
    return blocks;
  };

  // For notion: pre-fill WYSIWYG HTML from blocks on mount
  const [notionHtml, setNotionHtml] = useState('');

  const handleLmsThumbnailUpload = async (fileToUpload) => {
    if (!fileToUpload) return;
    if (fileToUpload.size > 2 * 1024 * 1024) {
      setErr('Thumbnail must be 2 MB or smaller.');
      return;
    }
    if (!isSupportedThumbnail(fileToUpload)) {
      setErr('Use a JPG, PNG, GIF, WEBP, or SVG thumbnail.');
      return;
    }
    setErr('');
    setThumbnailUploading(true);
    try {
      setThumbnailDimensions(await readImageDimensions(fileToUpload));
      const formData = new FormData();
      formData.append('file', fileToUpload);
      const response = await uploadLessonThumbnail(formData);
      const uploadedUrl = response?.data?.url || response?.url || '';
      if (!uploadedUrl) throw new Error('Thumbnail upload failed.');
      setLmsThumbnailUrl(uploadedUrl);
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Thumbnail upload failed.');
    } finally {
      setThumbnailUploading(false);
    }
  };

  useEffect(() => {
    if (isNotion) {
      setNotionHtml(notionToHtml(item?.contentBlocks || []));
    }
  }, [isNotion, item]);

  useEffect(() => {
    if (!item?.courseId || !showVideoLessonFields) return;
    let cancelled = false;
    const courseKey = item.courseId?._id || item.courseId;
    const loadPrerequisites = async () => {
      setLoadingPrerequisites(true);
      try {
        const curriculumRes = await api.get(`/training/course-content/curriculum/${courseKey}`);
        const curriculum = curriculumRes.data?.data || curriculumRes.data?.records || [];
        const topicIds = curriculum.flatMap((mod) => (mod.topics || []).map((topic) => ({
          topicId: topic._id,
          topicTitle: topic.title,
          moduleTitle: mod.title,
        })));
        const itemResponses = await Promise.all(topicIds.map((entry) =>
          api.get(`/training/course-content/topic-items/${entry.topicId}`).then((res) => ({
            ...entry,
            items: res.data?.data?.items || [],
          })).catch(() => ({ ...entry, items: [] }))
        ));
        if (cancelled) return;
        const options = itemResponses.flatMap((entry) => (entry.items || [])
          .filter((row) => String(row._id) !== String(item._id))
          .map((row) => ({
            value: String(row._id),
            label: `${entry.moduleTitle} > ${entry.topicTitle} > ${row.title}`,
          })));
        setPrerequisiteOptions(options);
      } catch (_) {
        if (!cancelled) setPrerequisiteOptions([]);
      }
      if (!cancelled) setLoadingPrerequisites(false);
    };
    loadPrerequisites();
    return () => { cancelled = true; };
  }, [item, showVideoLessonFields]);

  // For private internal_link: fetch the HTML from disk on open
  useEffect(() => {
    if (!isPrivateLink) return;
    const filePath = item.externalUrl.replace('private://', '');
    setFetchingHtml(true);
    api.get('/training/course-content/private-file-content', { params: { filePath } })
      .then(r => setPrivateHtml(r.data?.data?.content || ''))
      .catch(e => setErr(e.response?.data?.message || 'Failed to load file content.'))
      .finally(() => setFetchingHtml(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openWysiwyg = (currentHtml, onResult) => {
    const key = Date.now().toString();
    try { localStorage.setItem(`html_editor_input_${key}`, currentHtml); } catch (_) {}
    window.open(`/html-editor?key=${key}`, '_blank');
    const onMsg = (e) => {
      if (e.data?.type === 'html_editor_done' && e.data?.key === key) {
        const html = e.data.html ?? localStorage.getItem(`html_editor_result_${key}`) ?? currentHtml;
        onResult(html);
        try { localStorage.removeItem(`html_editor_input_${key}`); } catch (_) {}
        try { localStorage.removeItem(`html_editor_result_${key}`); } catch (_) {}
        window.removeEventListener('message', onMsg);
      }
    };
    window.addEventListener('message', onMsg);
  };

  const handleSave = async () => {
    if (!title.trim()) { setErr('Title is required.'); return; }
    if (showQuizLessonFields && !lmsQuizId) { setErr('Select a published quiz.'); return; }
    if (showVideoLessonFields && replacementVideo && !VIDEO_EXTENSIONS.has(fileExtension(replacementVideo))) {
      setErr('Use an MP4, WEBM, MOV, AVI, MKV, MPEG, or MPG video.');
      return;
    }
    if (showVideoLessonFields && replacementVideo?.size > 300 * 1024 * 1024) {
      setErr('Video must be 300 MB or smaller.');
      return;
    }
    if (thumbnailUploading) {
      setErr('Wait for the thumbnail upload to finish.');
      return;
    }
    setSaving(true); setErr('');
    try {
      const body = { title: title.trim(), description: desc.trim() };
      if (isLinkType) body.externalUrl = extUrl.trim();
      if (isHtml)    body.htmlContent  = editedHtml;
      if (isNotion)  body.contentBlocks = htmlToNotionBlocks(notionHtml);
      body.lmsLessonEnabled = (showVideoLessonFields || showQuizLessonFields) ? true : isLmsLesson;
      body.lmsLessonType = item?.type === 'video' ? 'uploaded_video' : item?.type === 'quiz' ? 'linked_quiz' : (['html', 'notion'].includes(item?.type) ? 'text' : '');
      if (showQuizLessonFields) {
        body.lmsQuizId = lmsQuizId;
        body.lmsQuizRequirement = lmsQuizRequirement;
      }
      body.lmsThumbnailUrl = lmsThumbnailUrl;
      body.lmsDurationSeconds = lmsDurationSeconds;
      body.lmsIsPreview = false;
      body.lmsAvailableFromPlan = lmsAvailableFromPlan;
      body.lmsRequiresItemIds = lmsRequiresItemIds.join(',');
      await api.patch(`/training/course-content/items/${item._id}`, body);

      if (showVideoLessonFields && replacementVideo) {
        const formData = new FormData();
        formData.append('file', replacementVideo);
        setVideoUploadProgress(0);
        await uploadFile(`/training/course-content/items/${item._id}/video-file`, formData, setVideoUploadProgress);
      }

      // For private internal_link: also write updated HTML back to the disk file
      if (isPrivateLink && privateHtml) {
        const filePath = item.externalUrl.replace('private://', '');
        await api.post('/training/course-content/private-file-save', { filePath, content: privateHtml });
      }

      onSaved?.();
      onClose();
    } catch(e) { setErr(e.response?.data?.message || 'Save failed.'); }
    setSaving(false);
    setVideoUploadProgress(0);
  };

  if (!item) return null;
  return (
    <BSModal
      show
      title={`Edit — ${item.title}`}
      size="lg"
      onClose={onClose}
      footer={<>
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || thumbnailUploading}>
          {saving ? <><span className="spinner-border spinner-border-sm me-1"/>Saving…</> : <><i className="bi bi-check-lg me-1"/>Save Changes</>}
        </button>
      </>}
    >
      {err && <div className="alert alert-danger py-2 small">{err}</div>}
      <div className="row g-3">
        {/* Title */}
        <div className="col-12">
          <label className="form-label">Title <span className="text-danger">*</span></label>
          <input className="form-control form-control-sm" value={title} onChange={e => setTitle(e.target.value)}/>
        </div>

        {/* Description */}
        <div className="col-12">
          <label className="form-label">Description</label>
          <input className="form-control form-control-sm" value={desc} onChange={e => setDesc(e.target.value)}/>
        </div>

        {showQuizLessonFields && (
          <div className="col-12">
            <div className="border rounded p-3 bg-light">
              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label">Quiz <span className="text-danger">*</span></label>
                  <select className="form-select form-select-sm" value={lmsQuizId} disabled={quizzesLoading} onChange={(event) => setLmsQuizId(event.target.value)}>
                    <option value="">{quizzesLoading ? 'Loading quizzes...' : 'Select a published quiz'}</option>
                    {quizzes.map((quiz) => (
                      <option key={quiz._id} value={quiz._id} disabled={quiz.status !== 'published'}>
                        {quiz.title} ({quiz.category || 'practice'}){quiz.status !== 'published' ? ` - ${quiz.status}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Attendance</label>
                  <select className="form-select form-select-sm" value={lmsQuizRequirement} onChange={(event) => setLmsQuizRequirement(event.target.value)}>
                    <option value="optional">Optional</option>
                    <option value="compulsory">Compulsory</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* URL (link / non-private internal_link) */}
        {isLinkType && (
          <div className="col-12">
            <label className="form-label">URL</label>
            <input className="form-control form-control-sm" value={extUrl} onChange={e => setExtUrl(e.target.value)} placeholder="https://..."/>
          </div>
        )}

        {/* HTML content editor */}
        {isHtml && (
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label mb-0">HTML Content</label>
              <button type="button" className="btn btn-outline-primary btn-sm py-0 px-2"
                onClick={() => openWysiwyg(editedHtml, setEditedHtml)}>
                <i className="bi bi-pencil-square me-1"/>Edit in WYSIWYG
              </button>
            </div>
            {editedHtml ? (
              <div className="border rounded p-3" style={{ maxHeight: '35vh', overflowY: 'auto', background: '#fafbfc' }}
                dangerouslySetInnerHTML={{ __html: editedHtml }}/>
            ) : (
              <div className="text-muted small border rounded p-3 text-center">No HTML content yet.</div>
            )}
          </div>
        )}

        {/* Notion blocks — WYSIWYG editor */}
        {isNotion && (
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label mb-0">
                <i className="bi bi-journal-text me-1 text-dark"/>Notion Content
              </label>
              <button type="button" className="btn btn-outline-primary btn-sm py-0 px-2"
                onClick={() => openWysiwyg(notionHtml, setNotionHtml)}>
                <i className="bi bi-pencil-square me-1"/>Edit in WYSIWYG
              </button>
            </div>
            {notionHtml ? (
              <div className="border rounded p-3" style={{ maxHeight: '35vh', overflowY: 'auto', background: '#fafbfc' }}
                dangerouslySetInnerHTML={{ __html: notionHtml }}/>
            ) : (
              <div className="text-muted small border rounded p-3 text-center">No content yet.</div>
            )}
          </div>
        )}

        {/* Private internal_link — edit the disk file */}
        {isPrivateLink && (
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label mb-0">
                <i className="bi bi-file-earmark-code me-1 text-primary"/>
                {item.externalUrl.replace('private://', '')}
              </label>
              <button type="button" className="btn btn-outline-primary btn-sm py-0 px-2"
                onClick={() => openWysiwyg(privateHtml, setPrivateHtml)}
                disabled={fetchingHtml}>
                {fetchingHtml
                  ? <span className="spinner-border spinner-border-sm"/>
                  : <><i className="bi bi-pencil-square me-1"/>Edit in WYSIWYG</>}
              </button>
            </div>
            {fetchingHtml ? (
              <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary"/></div>
            ) : privateHtml ? (
              <div className="border rounded p-3" style={{ maxHeight: '35vh', overflowY: 'auto', background: '#fafbfc' }}
                dangerouslySetInnerHTML={{ __html: privateHtml }}/>
            ) : (
              <div className="text-muted small border rounded p-3 text-center">File is empty or could not be loaded.</div>
            )}
            <div className="form-text">Saving will update the HTML file on the server.</div>
          </div>
        )}

        {/* Read-only info for file-based types */}
        {['pdf','document'].includes(item.type) && (
          <div className="col-12">
            <div className="alert alert-info py-2 small mb-0">
              <i className="bi bi-info-circle me-1"/>
              File content cannot be changed after upload. You can update the title and description above.
              {item.fileName && <span className="ms-2 text-muted">File: {item.fileName}</span>}
            </div>
          </div>
        )}
        {showVideoLessonFields && (
          <div className="col-12">
            <label className="form-label">Replace Video</label>
            <input
              className="form-control form-control-sm"
              type="file"
              accept=".mp4,.webm,.mov,.avi,.mkv,.mpeg,.mpg,video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-matroska,video/mpeg"
              disabled={saving}
              onChange={(e) => { setReplacementVideo(e.target.files?.[0] || null); setErr(''); }}
            />
            <div className={`form-text ${replacementVideo ? 'text-success' : 'text-muted'}`}>
              <i className={`bi ${replacementVideo ? 'bi-check-circle-fill' : 'bi-camera-video'} me-1`} />
              {replacementVideo
                ? `New video ready: ${replacementVideo.name} (${fmtSize(replacementVideo.size)})`
                : `Current video uploaded${item.fileName ? `: ${item.fileName}` : ''}. Select a file only when replacing it.`}
            </div>
            {videoUploadProgress > 0 && <div className="form-text text-primary">Uploading replacement video: {videoUploadProgress}%</div>}
          </div>
        )}
        {showVideoLessonFields && (
          <div className="col-12">
            <div className="border rounded p-3" style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Thumbnail Image <span className="text-muted fw-normal">(Optional)</span></label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.jfif,.png,.gif,.webp,.svg,image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                      className="form-control form-control-sm"
                      disabled={saving || thumbnailUploading}
                      onChange={async (e) => {
                        const selected = e.target.files?.[0];
                        if (selected) await handleLmsThumbnailUpload(selected);
                      }}
                    />
                    <div className={`form-text ${lmsThumbnailUrl && !thumbnailUploading ? 'text-success fw-semibold' : ''}`}>
                      {thumbnailUploading
                        ? 'Uploading thumbnail...'
                        : (lmsThumbnailUrl
                          ? `Thumbnail uploaded successfully${thumbnailDimensions ? ` (${thumbnailDimensions.width}x${thumbnailDimensions.height})` : ''}.`
                          : 'Optional. Recommended: 1280x720 JPG/PNG, under 2 MB.')}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Duration (seconds)</label>
                    <input type="number" min="0" className="form-control form-control-sm" value={lmsDurationSeconds} onChange={e => setLmsDurationSeconds(e.target.value)} />
                    <div className="form-text">Video upload limit: 300 MB. Enter the lesson duration in seconds.</div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Available From Plan</label>
                    <input className="form-control form-control-sm" value={lmsAvailableFromPlan} onChange={e => setLmsAvailableFromPlan(e.target.value)} placeholder="Basic / Premium / Custom" />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Prerequisite Lessons</label>
                    {loadingPrerequisites ? (
                      <div className="text-muted small"><span className="spinner-border spinner-border-sm me-2" />Loading available lessons…</div>
                    ) : prerequisiteOptions.length ? (
                      <div className="border rounded p-2 bg-white" style={{ maxHeight: 220, overflowY: 'auto' }}>
                        <div className="d-flex flex-column gap-2">
                          {prerequisiteOptions.map((option) => {
                            const checked = lmsRequiresItemIds.includes(option.value);
                            return (
                              <label key={`edit-prereq-${option.value}`} className="form-check border rounded p-2">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    setLmsRequiresItemIds((prev) => (
                                      e.target.checked
                                        ? [...prev, option.value]
                                        : prev.filter((value) => value !== option.value)
                                    ));
                                  }}
                                />
                                <span className="form-check-label ms-2 small">{option.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted small">No other content items are available yet for prerequisites.</div>
                    )}
                  </div>
                </div>
            </div>
          </div>
        )}
      </div>
    </BSModal>
  );
};

// ─── Topic Content Panel ──────────────────────────────────────────────────────
const TopicContentPanel = ({ topicId, canWrite, canAdmin, onUploadClick, refreshKey }) => {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [alert, setAlert]   = useState({ show:false, msg:'', type:'success' });

  const fetchItems = useCallback(async () => {
    if (!topicId) return;
    setLoading(true);
    try {
      const r = await api.get(`/training/course-content/topic-items/${topicId}`);
      setItems(r.data?.data?.items || []);
    } catch(_) {}
    setLoading(false);
  }, [topicId]);

  useEffect(() => { fetchItems(); }, [fetchItems, refreshKey]);

  const handleApprove = async (item) => {
    try { await api.patch(`/training/course-content/items/${item._id}/approve`); fetchItems(); } catch(_) {}
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim() || rejectReason.trim().length < 5) return;
    try { await api.patch(`/training/course-content/items/${rejectItem._id}/reject`, { rejectionReason: rejectReason }); setRejectItem(null); setRejectReason(''); fetchItems(); } catch(_) {}
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      const r = await api.delete(`/training/course-content/items/${item._id}`);
      if (r.data?.requestSubmitted) {
        setAlert({ show: true, msg: 'Delete request submitted. Admin approval pending.', type: 'info' });
      } else {
        setAlert({ show: true, msg: r.data?.message || 'Content deleted.', type: 'success' });
      }
      fetchItems();
    } catch(e) {
      setAlert({ show: true, msg: e.response?.data?.message || 'Failed to delete.', type: 'danger' });
    }
    setTimeout(() => setAlert(a => ({ ...a, show: false })), 3000);
  };

  const hasNotionItems = items.some(i => i.type === 'notion' && i.notionUrl);

  const handleRefreshNotion = async () => {
    setRefreshing(true);
    try {
      await api.post(`/training/course-content/refresh-notion/${topicId}`);
      fetchItems();
    } catch(e) {
      // silently fail — items list still shows
    }
    setRefreshing(false);
  };

  if (!topicId) return (
    <div className="d-flex align-items-center justify-content-center h-100 text-muted">
      <div className="text-center"><i className="bi bi-arrow-left fs-1 d-block mb-2"/>Select a topic to view content</div>
    </div>
  );

  return (
    <div>
      {alert.show && <div className={`alert alert-${alert.type} py-2 mb-3`}>{alert.msg}</div>}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Topic Content ({items.length})</h6>
        <div className="d-flex gap-2">
          {canWrite && hasNotionItems && (
            <button className="btn btn-outline-secondary btn-sm" onClick={handleRefreshNotion} disabled={refreshing} title="Re-fetch content from Notion for this topic">
              {refreshing ? <><span className="spinner-border spinner-border-sm me-1"/>Refreshing…</> : <><i className="bi bi-arrow-repeat me-1"/>Refresh from Notion</>}
            </button>
          )}
          {canWrite && (
            <button className="btn btn-primary btn-sm" onClick={onUploadClick}>
              <i className="bi bi-upload me-1"/>Upload
            </button>
          )}
        </div>
      </div>
      {loading ? (
        <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"/></div>
      ) : items.length === 0 ? (
        <div className="text-center py-4 text-muted"><i className="bi bi-folder2-open fs-2 d-block mb-2"/>No content yet</div>
      ) : (
        items.map(item => (
          <ContentItemRow key={item._id} item={item} canWrite={canWrite} canAdmin={canAdmin}
            onApprove={handleApprove} onReject={setRejectItem} onDelete={handleDelete}
            onEdit={canWrite ? setEditItem : undefined}/>
        ))
      )}

      {/* Reject Modal */}
      <BSModal show={!!rejectItem} title="Reject Content" onClose={() => { setRejectItem(null); setRejectReason(''); }}
        footer={<>
          <button className="btn btn-secondary btn-sm" onClick={() => { setRejectItem(null); setRejectReason(''); }}>Cancel</button>
          <button className="btn btn-danger btn-sm" onClick={handleRejectSubmit}>Reject</button>
        </>}>
        <p className="mb-2">Rejecting: <strong>{rejectItem?.title}</strong></p>
        <label className="form-label">Reason <span className="text-danger">*</span></label>
        <textarea className="form-control" rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Explain the rejection (min 5 chars)"/>
      </BSModal>

      {editItem && (
        <EditContentModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={() => { setEditItem(null); fetchItems(); }}
        />
      )}
    </div>
  );
};

// ─── Pending Approvals Tab ─────────────────────────────────────────────────────
const PendingTab = ({ canAdmin, canWrite }) => {
  const [pendingItems, setPendingItems]   = useState([]);
  const [deleteRequests, setDeleteRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editItem, setEditItem] = useState(null);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/course-content/pending');
      setPendingItems(r.data?.data?.pendingItems || []);
      setDeleteRequests(r.data?.data?.deleteRequests || []);
    } catch(_) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const approve = async (item) => {
    try { await api.patch(`/training/course-content/items/${item._id}/approve`); fetchPending(); } catch(_) {}
  };

  const approveDelete = async (item) => {
    try { await api.patch(`/training/course-content/items/${item._id}/approve-delete`); fetchPending(); } catch(_) {}
  };

  const rejectDeleteSubmit = async () => {
    try { await api.patch(`/training/course-content/items/${rejectItem._id}/reject-delete`); setRejectItem(null); setRejectReason(''); fetchPending(); } catch(_) {}
  };

  const rejectSubmit = async () => {
    try { await api.patch(`/training/course-content/items/${rejectItem._id}/reject`, { rejectionReason: rejectReason }); setRejectItem(null); setRejectReason(''); fetchPending(); } catch(_) {}
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="fw-semibold">Pending Approvals <span className="badge bg-warning text-dark ms-1">{pendingItems.length}</span></span>
        <button className="btn btn-outline-secondary btn-sm" onClick={fetchPending}><i className="bi bi-arrow-clockwise"/></button>
      </div>
      {loading ? (
        <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"/></div>
      ) : (
        <>
          {pendingItems.length === 0 && deleteRequests.length === 0 && (
            <div className="text-center py-5 text-muted"><i className="bi bi-check-circle fs-2 d-block mb-2"/>No pending content</div>
          )}

          {/* Delete Requests */}
          {deleteRequests.length > 0 && (
            <div className="mb-3">
              <div className="alert alert-danger py-2 d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-exclamation-triangle-fill"/>Delete Requests ({deleteRequests.length})
              </div>
              {deleteRequests.map(item => (
                <div key={item._id} className="card mb-2 border border-danger">
                  <div className="card-body py-2 px-3">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                      <div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <i className={`bi ${typeIcon(item.type)} text-${typeColor(item.type)}`}/>
                          <span className="fw-semibold small">{item.title}</span>
                          <span className="badge bg-danger-subtle text-danger border border-danger small">
                            <i className="bi bi-trash me-1"/>Delete Request
                          </span>
                          <CourseNameBadge courseName={item.courseId?.courseName || item.courseId?.name} courseColor={item.courseId?.courseColor} compact />
                          <span className="badge bg-light text-dark border small">{item.moduleId?.title}</span>
                          <span className="badge bg-light text-dark border small">{item.topicId?.title}</span>
                        </div>
                        <div className="text-muted" style={{fontSize:'0.72rem'}}>
                          Requested by {item.deleteRequestedBy?.firstName} {item.deleteRequestedBy?.lastName} · {item.deleteRequestedAt ? new Date(item.deleteRequestedAt).toLocaleDateString() : '—'}
                        </div>
                      </div>
                      <div className="d-flex gap-1">
                        <button className="btn btn-success btn-sm" onClick={() => approveDelete(item)} title="Approve delete">
                          <i className="bi bi-check-lg me-1"/>Approve
                        </button>
                        <button className="btn btn-outline-danger btn-sm" onClick={() => setRejectItem(item)} title="Reject and retain content">
                          <i className="bi bi-x-lg"/>Reject
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pending Content Approvals */}
          {pendingItems.map(item => (
            <div key={item._id} className="card mb-2">
              <div className="card-body py-2 px-3">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <i className={`bi ${typeIcon(item.type)} text-${typeColor(item.type)}`}/>
                      <span className="fw-semibold small">{item.title}</span>
                      <CourseNameBadge courseName={item.courseId?.courseName || item.courseId?.name} courseColor={item.courseId?.courseColor} compact />
                      <span className="badge bg-light text-dark border small">{item.moduleId?.title}</span>
                      <span className="badge bg-light text-dark border small">{item.topicId?.title}</span>
                    </div>
                    <div className="text-muted" style={{fontSize:'0.72rem'}}>
                      By {item.uploadedBy?.firstName} {item.uploadedBy?.lastName} · {new Date(item.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="d-flex gap-1">
                    {canWrite && (
                      <button className="btn btn-outline-primary btn-sm" onClick={() => setEditItem(item)} title="Edit content">
                        <i className="bi bi-pencil"/>
                      </button>
                    )}
                    <button className="btn btn-outline-success btn-sm" onClick={() => approve(item)}><i className="bi bi-check-lg"/></button>
                    <button className="btn btn-outline-danger btn-sm" onClick={() => setRejectItem(item)}><i className="bi bi-x-lg"/></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      <BSModal show={!!rejectItem} title={rejectItem?.deleteStatus === 'pending' ? 'Reject Delete Request' : 'Reject Content'} onClose={() => { setRejectItem(null); setRejectReason(''); }}
        footer={<>
          <button className="btn btn-secondary btn-sm" onClick={() => { setRejectItem(null); setRejectReason(''); }}>Cancel</button>
          <button className="btn btn-danger btn-sm" onClick={rejectItem?.deleteStatus === 'pending' ? rejectDeleteSubmit : rejectSubmit}>
            {rejectItem?.deleteStatus === 'pending' ? 'Reject Delete' : 'Reject'}
          </button>
        </>}>
        <p className="mb-2">
          {rejectItem?.deleteStatus === 'pending'
            ? <>Rejecting delete request for: <strong>{rejectItem?.title}</strong>. The content will be retained.</>
            : <>Rejecting: <strong>{rejectItem?.title}</strong></>}
        </p>
        <label className="form-label">Reason <span className="text-danger">*</span></label>
        <textarea className="form-control" rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}/>
      </BSModal>

      {editItem && (
        <EditContentModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={() => { setEditItem(null); fetchPending(); }}
        />
      )}
    </div>
  );
};

// ─── Reusable Content Bank ────────────────────────────────────────────────────
const ReusableBankPanel = ({ courses, canWrite, canAdmin }) => {
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [collapsed, setCollapsed]   = useState(false);
  const [filterCourse, setFilterCourse] = useState('');
  const [filterType, setFilterType]     = useState('');
  const [search, setSearch]             = useState('');
  const [rejectItem, setRejectItem]     = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editItem, setEditItem]         = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCourse) params.courseId = filterCourse;
      if (filterType)   params.type     = filterType;
      if (search.trim()) params.search  = search.trim();
      const r = await api.get('/training/course-content/reusable', { params });
      setItems(r.data?.data?.items || []);
    } catch(_) {}
    setLoading(false);
  }, [filterCourse, filterType, search]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleApprove = async (item) => {
    try { await api.patch(`/training/course-content/items/${item._id}/approve`); fetchItems(); } catch(_) {}
  };
  const handleRejectSubmit = async () => {
    if (!rejectReason.trim() || rejectReason.trim().length < 5) return;
    try { await api.patch(`/training/course-content/items/${rejectItem._id}/reject`, { rejectionReason: rejectReason }); setRejectItem(null); setRejectReason(''); fetchItems(); } catch(_) {}
  };
  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try { await api.delete(`/training/course-content/items/${item._id}`); fetchItems(); } catch(_) {}
  };

  return (
    <div className="card mb-3 border-success">
      <div
        className="card-header d-flex align-items-center gap-2 py-2 bg-success-subtle"
        role="button"
        onClick={() => setCollapsed(v => !v)}
      >
        <i className="bi bi-arrow-repeat text-success fs-5"/>
        <span className="fw-semibold text-success-emphasis">Reusable Content Bank</span>
        <span className="badge bg-success ms-1">{items.length}</span>
        <span className="text-muted small ms-1">— all content marked as reusable across courses</span>
        <div className="ms-auto d-flex gap-1" onClick={e => e.stopPropagation()}>
          <button className="btn btn-outline-secondary btn-sm" onClick={fetchItems} title="Refresh">
            <i className="bi bi-arrow-clockwise"/>
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setCollapsed(v => !v)}>
            <i className={`bi bi-chevron-${collapsed ? 'down' : 'up'}`}/>
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="card-body p-2">
          <div className="row g-2 mb-2">
            <div className="col-md-4">
              <input
                className="form-control form-control-sm"
                placeholder="Search by title..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select className="form-select form-select-sm" value={filterCourse} onChange={e => setFilterCourse(e.target.value)}>
                <option value="">All Courses</option>
                {courses.map(c => <option key={c._id} value={c._id}>{c.courseName || c.name}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <select className="form-select form-select-sm" value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="">All Types</option>
                {['pdf','video','document','link','internal_link','html','notion','quiz'].map(t =>
                  <option key={t} value={t}>{t === 'notion' ? 'Notion Import' : t === 'quiz' ? 'Quiz' : t}</option>
                )}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-success"/></div>
          ) : items.length === 0 ? (
            <div className="text-center py-3 text-muted small">
              <i className="bi bi-arrow-repeat fs-3 d-block mb-1"/>
              No reusable content yet. Upload content to add it to the bank.
            </div>
          ) : (
            items.map(item => (
              <div key={item._id} className="mb-2">
                <div className="text-muted small mb-1 ps-1">
                  <i className="bi bi-diagram-3 me-1"/>
                  <CourseNameBadge courseName={item.courseId?.courseName || '—'} courseColor={item.courseId?.courseColor} compact className="me-1" />
                  {' › '}{item.moduleId?.title || '—'}
                  {' › '}{item.topicId?.title || '—'}
                </div>
                <ContentItemRow
                  item={item}
                  canWrite={canWrite}
                  canAdmin={canAdmin}
                  onApprove={handleApprove}
                  onReject={setRejectItem}
                  onDelete={handleDelete}
                  onEdit={canWrite ? setEditItem : undefined}
                />
              </div>
            ))
          )}
        </div>
      )}

      <BSModal
        show={!!rejectItem}
        title="Reject Content"
        onClose={() => { setRejectItem(null); setRejectReason(''); }}
        footer={<>
          <button className="btn btn-secondary btn-sm" onClick={() => { setRejectItem(null); setRejectReason(''); }}>Cancel</button>
          <button className="btn btn-danger btn-sm" onClick={handleRejectSubmit}>Reject</button>
        </>}
      >
        <p className="mb-2">Rejecting: <strong>{rejectItem?.title}</strong></p>
        <label className="form-label">Reason <span className="text-danger">*</span></label>
        <textarea className="form-control" rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Explain the rejection (min 5 chars)"/>
      </BSModal>

      {editItem && (
        <EditContentModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={() => { setEditItem(null); fetchItems(); }}
        />
      )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const StudentCourseContentView = ({ courseId }) => {
  const [curriculum, setCurriculum] = useState([]);
  const [openModule, setOpenModule] = useState(null);
  const [openTopic, setOpenTopic] = useState(null);
  const [openItem, setOpenItem] = useState({});
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!courseId) { setCurriculum([]); setOpenModule(null); setOpenTopic(null); setLoadError(''); return; }
    setLoading(true);
    setLoadError('');
    api.get(`/training/course-content/curriculum/${courseId}`)
      .then(r => {
        const payload = r.data?.data || r.data || {};
        const list = Array.isArray(payload.curriculum)
          ? payload.curriculum
          : (Array.isArray(payload.records) ? payload.records : []);
        setCurriculum(list);
        setOpenModule(list[0]?._id || null);
      })
      .catch((error) => {
        setCurriculum([]);
        setLoadError(error.response?.data?.message || 'Could not load this course content.');
      })
      .finally(() => setLoading(false));
  }, [courseId]);

  const loadTopic = async (topicId) => {
    setOpenTopic(prev => (prev === topicId ? null : topicId));
    if (items[topicId]) return;
    try {
      const r = await api.get(`/training/course-content/topic-items/${topicId}`);
      setItems(prev => ({ ...prev, [topicId]: r.data?.data?.items || [] }));
    } catch (_) {
      setItems(prev => ({ ...prev, [topicId]: [] }));
    }
  };

  if (!courseId) return <div className="student-course-content-empty"><i className="bi bi-journal-x"/><h2>No enrolled courses</h2><p>Your enrolled course content will appear here.</p></div>;
  if (loading) return <div className="student-course-content-loading"><div className="spinner-border spinner-border-sm text-primary" /><span>Loading your course content...</span></div>;
  if (loadError) return <div className="student-course-content-empty"><i className="bi bi-exclamation-circle"/><h2>Could not load course content</h2><p>{loadError}</p></div>;
  if (!curriculum.length) return <div className="student-course-content-empty"><i className="bi bi-folder2-open"/><h2>Content is being prepared</h2><p>No learning materials are available for this course yet.</p></div>;

  return (
    <div className="student-course-curriculum">
      {curriculum.map((mod, idx) => {
        const expanded = openModule === mod._id;
        return (
          <section className={`student-course-module ${expanded ? 'is-open' : ''}`} key={mod._id}>
              <button className="student-course-module-toggle" onClick={() => setOpenModule(expanded ? null : mod._id)}>
                <span className="student-course-module-copy">
                  <span className="student-course-module-number">{String(idx + 1).padStart(2, '0')}</span>
                  <span><strong>{mod.title}</strong><small>{mod.topics?.length || 0} topics</small></span>
                </span>
                <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} />
              </button>
              {expanded && (
                <div className="student-course-topics">
                  {(mod.topics || []).map(topic => {
                    const topicOpen = openTopic === topic._id;
                    const topicItems = items[topic._id] || [];
                    return (
                      <div className={`student-course-topic ${topicOpen ? 'is-open' : ''}`} key={topic._id}>
                        <button className="student-course-topic-toggle" onClick={() => loadTopic(topic._id)}>
                          <span className="student-course-topic-copy">
                            <i className="bi bi-play-circle" />
                            <span><strong>{topic.title}</strong><small>{topic.contentCount || 0} learning items</small></span>
                          </span>
                          <i className={`bi bi-chevron-${topicOpen ? 'up' : 'right'}`} />
                        </button>
                        {topicOpen && (
                          <div className="student-course-materials">
                            {topicItems.length === 0 ? (
                              <div className="student-course-topic-empty">No approved materials for this topic yet.</div>
                            ) : (
                              <div className="student-course-material-list">
                                {topicItems.map(item => (
                                  <div className="student-course-material" key={item._id}>
                                      <div className="d-flex align-items-start gap-2">
                                        <span className={`student-course-material-icon text-${typeColor(item.type)}`}><i className={`bi ${typeIcon(item.type)}`} /></span>
                                        <div className="flex-grow-1">
                                          <div className="student-course-material-title">{item.title}</div>
                                          <div className="text-muted small text-capitalize">
                                            {item.type}{item.fileSize ? ` - ${fmtSize(item.fileSize)}` : ''}
                                          </div>
                                          {item.description && <div className="text-muted small mt-1">{item.description}</div>}
                                          <button
                                            type="button"
                                            className="student-course-open-button"
                                            onClick={() => setOpenItem(prev => ({
                                              ...prev,
                                              [item._id]: !prev[item._id],
                                            }))}
                                          >
                                            <i className={`bi ${openItem[item._id] ? 'bi-chevron-up' : 'bi-eye'} me-1`} />
                                            {openItem[item._id] ? 'Hide Content' : 'Open Content'}
                                          </button>
                                        </div>
                                      </div>
                                      {openItem[item._id] && (
                                        <div className="student-course-material-preview">
                                          <InlineContentPreview item={item} interactiveQuiz />
                                        </div>
                                      )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
          </section>
        );
      })}
    </div>
  );
};

const CourseDoubtsPanel = ({ courseId, onAlert }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyingId, setReplyingId] = useState('');
  const [replyDrafts, setReplyDrafts] = useState({});

  const loadDoubts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/training/course-discussions', { params: courseId ? { courseId } : {} });
      setRecords(response.data?.records || response.data?.data || []);
    } catch (error) {
      setRecords([]);
      onAlert(error.response?.data?.message || 'Could not load student doubts.', 'danger');
    }
    setLoading(false);
  }, [courseId, onAlert]);

  useEffect(() => { loadDoubts(); }, [loadDoubts]);

  const submitReply = async (discussionId) => {
    const message = String(replyDrafts[discussionId] || '').trim();
    if (message.length < 2) return;
    setReplyingId(discussionId);
    try {
      await api.post(`/training/course-discussions/${discussionId}/replies`, { message });
      setReplyDrafts(prev => ({ ...prev, [discussionId]: '' }));
      onAlert('Reply posted successfully.');
      await loadDoubts();
    } catch (error) {
      onAlert(error.response?.data?.message || 'Could not post the reply.', 'danger');
    }
    setReplyingId('');
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white d-flex justify-content-between align-items-center gap-2">
        <div><div className="fw-semibold">Student Doubts</div><div className="small text-muted">Questions submitted from course lessons.</div></div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={loadDoubts} disabled={loading}><i className="bi bi-arrow-clockwise" /></button>
      </div>
      <div className="card-body">
        {loading ? <div className="text-center py-5"><span className="spinner-border spinner-border-sm text-primary" /></div> : null}
        {!loading && !records.length ? <div className="text-center text-muted py-5"><i className="bi bi-chat-square-text fs-2 d-block mb-2" />No student doubts have been submitted{courseId ? ' for this course' : ''}.</div> : null}
        {!loading && records.length ? (
          <div className="d-flex flex-column gap-3">
            {records.map(record => {
              const studentName = [record.student?.firstName, record.student?.lastName].filter(Boolean).join(' ') || 'Student';
              return (
                <article key={record._id} className="border rounded-3 p-3 bg-white">
                  <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div className="d-flex align-items-center gap-2 flex-wrap"><strong>{studentName}</strong><span className={`badge ${record.status === 'answered' ? 'text-bg-success' : 'text-bg-warning'}`}>{record.status === 'answered' ? 'Answered' : 'Open'}</span></div>
                      <div className="small text-muted mt-1"><i className="bi bi-mortarboard me-1" />{record.courseId?.courseName || 'Course'}{record.lessonTitle ? ` | ${record.lessonTitle}` : ''}</div>
                      <div className="small text-muted mt-1 d-flex flex-wrap gap-3">
                        {record.student?.phone ? <span><i className="bi bi-telephone me-1" />{record.student.phone}</span> : null}
                        {record.student?.email ? <span><i className="bi bi-envelope me-1" />{record.student.email}</span> : null}
                      </div>
                    </div>
                    <small className="text-muted">{record.createdAt ? new Date(record.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}</small>
                  </div>
                  <div className="mt-3 p-3 rounded-3" style={{ background: '#f8fafc', color: '#334155', whiteSpace: 'pre-wrap' }}>{record.message}</div>
                  {(record.replies || []).map(reply => (
                    <div key={reply._id} className="mt-2 ms-md-4 p-2 border-start border-3 border-primary bg-primary-subtle rounded-end-3">
                      <div className="small fw-semibold">{[reply.author?.firstName, reply.author?.lastName].filter(Boolean).join(' ') || 'Faculty'}</div>
                      <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{reply.message}</div>
                    </div>
                  ))}
                  <div className="input-group input-group-sm mt-3">
                    <input className="form-control" maxLength={2000} placeholder="Write a helpful reply..." value={replyDrafts[record._id] || ''} onChange={event => setReplyDrafts(prev => ({ ...prev, [record._id]: event.target.value }))} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); submitReply(record._id); } }} />
                    <button type="button" className="btn btn-primary" disabled={replyingId === record._id || String(replyDrafts[record._id] || '').trim().length < 2} onClick={() => submitReply(record._id)}>{replyingId === record._id ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-send me-1" />Reply</>}</button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default function CourseContentPage() {
  const { user } = useAuth();
  const role = String(user?.roleKey || user?.role || '').trim().toLowerCase();
  // Teaching role keys vary across industries / legacy seedings — see notes
  // in attendance.js / Assessments.js. Without 'teacher' and 'trainer' the
  // Upload Content button silently disappears for users seeded on the
  // legacy "Trainer" role (still roleKey='teacher' for back-compat).
  const canWrite = ['admin','manager','team_lead','faculty','teacher','trainer'].includes(role);
  const canAdmin = ['admin','manager','team_lead'].includes(role);
  const isStudent = role === 'student';

  // Auto-select the course when arriving from another page via
  //   /course-content?courseId=<id>
  // — supports the "View Syllabus" link in the Courses list and any
  // other deep-link that wants to jump directly into a course's TOC.
  const location = useLocation();
  const initialCourseId = (() => {
    const params = new URLSearchParams(location.search);
    return params.get('courseId') || '';
  })();

  const [courses, setCourses]       = useState([]);
  const [courseId, setCourseId]     = useState(initialCourseId);
  const [selectedModId, setSelectedModId] = useState(null);
  const [selectedTopId, setSelectedTopId] = useState(null);
  const initialTab = new URLSearchParams(location.search).get('tab') === 'doubts' && canWrite ? 'doubts' : 'content';
  const [activeTab, setActiveTab]   = useState(initialTab); // content | reusable | pending | myuploads | doubts
  const [showUpload, setShowUpload] = useState(false);
  const [myItems, setMyItems]       = useState([]);
  const [myLoading, setMyLoading]   = useState(false);
  const [coursesError, setCoursesError] = useState('');
  const [topicRefreshKey, setTopicRefreshKey] = useState(0);
  const [alert, setAlert]           = useState({ show:false, msg:'', type:'success' });

  const showAlert = (msg, type='success') => setAlert({ show:true, msg, type });

  useEffect(() => {
    setCoursesError('');
    const request = isStudent
      ? api.get('/training/course-content/my-courses')
      : api.get('/training/courses', { params: { limit: 200 } });
    request
      .then(r => {
        const list = isStudent && Array.isArray(r.data?.data?.courses) ? r.data.data.courses
                   : Array.isArray(r.data?.data) ? r.data.data
                   : Array.isArray(r.data?.records) ? r.data.records
                   : Array.isArray(r.data?.data?.records) ? r.data.data.records
                   : [];
        setCourses(list);
        if (isStudent) {
          setCourseId((currentId) => {
            const currentIsEnrolled = list.some((course) => String(course._id) === String(currentId));
            return currentIsEnrolled ? currentId : (list[0]?._id || '');
          });
        }
      })
      .catch((e) => {
        // Surface a real reason instead of silently rendering an empty
        // dropdown. 403 here usually means the role's matrix doesn't grant
        // the `courses` module — but anyone in the tenant should be able
        // to LIST courses for the Course Content page; only create/edit
        // need elevated rights.
        const status = e.response?.status;
        const msg = e.response?.data?.message || e.message;
        setCoursesError(
          status === 403
            ? `Could not load courses: ${msg || 'permission denied'}.`
            : `Could not load courses: ${msg || 'network error'}.`
        );
        setCourses([]);
      });
  }, [isStudent]);

  const fetchMyItems = useCallback(async () => {
    setMyLoading(true);
    try {
      const r = await api.get('/training/course-content/faculty/items');
      setMyItems(r.data?.data?.items || []);
    } catch(_) {}
    setMyLoading(false);
  }, []);

  useEffect(() => { if (activeTab === 'myuploads') fetchMyItems(); }, [activeTab, fetchMyItems]);

  return (
    <div className={`container-fluid py-3 ${isStudent ? 'student-course-content-page' : ''}`}>
      <AlertToast message={alert.msg} type={alert.type} show={alert.show} onClose={() => setAlert(p => ({ ...p, show:false }))}/>

      <PageHeader
        title="Course Content"
        icon="bi-collection-play"
        subtitle={isStudent ? 'Continue learning from your enrolled courses' : 'Manage modules, topics and learning materials'}
        actions={canWrite && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowUpload(true)}>
            <i className="bi bi-upload me-1"/>Upload Content
          </button>
        )}
      />

      {/* Course selector */}
      <div className={isStudent ? 'student-enrolled-course-selector' : 'card mb-3'}>
        <div className="card-body py-2">
          <div className="row align-items-center g-2">
            <div className="col-auto"><label className="form-label mb-0 fw-semibold">{isStudent ? 'Enrolled course' : 'Course'}</label></div>
            <div className={isStudent ? 'col' : 'col-md-4'}>
              {isStudent && courses.length === 1 ? (
                <div className="student-selected-course"><i className="bi bi-mortarboard"/><span>{courses[0].courseName || courses[0].name}</span><small>Currently selected</small></div>
              ) : (
                <select className="form-select form-select-sm" value={courseId} onChange={e => { setCourseId(e.target.value); setSelectedModId(null); setSelectedTopId(null); }}>
                  <option value="">Select Course</option>
                  {courses.map(c => <option key={c._id} value={c._id}>{c.courseName || c.name}</option>)}
                </select>
              )}
            </div>
            <div className="col-auto small text-muted">
              {coursesError
                ? <span className="text-danger"><i className="bi bi-exclamation-triangle me-1"/>{coursesError}</span>
                : <>{courses.length} enrolled course{courses.length === 1 ? '' : 's'}</>}
            </div>
          </div>
        </div>
      </div>

      {/* Reusable Content Bank — hidden for students */}
      {/* Tabs */}
      <ul className={`nav nav-tabs mb-3 ${isStudent ? 'student-course-content-tabs' : ''}`}>
        <li className="nav-item"><button className={`nav-link ${activeTab==='content'?'active':''}`} onClick={() => setActiveTab('content')}>
          <i className="bi bi-collection me-1"/>Content
        </button></li>
        {canWrite && <li className="nav-item"><button className={`nav-link ${activeTab==='reusable'?'active':''}`} onClick={() => setActiveTab('reusable')}>
          <i className="bi bi-recycle me-1"/>Reusable Content
        </button></li>}
        {canAdmin && <li className="nav-item"><button className={`nav-link ${activeTab==='pending'?'active':''}`} onClick={() => setActiveTab('pending')}>
          <i className="bi bi-clock me-1"/>Pending Approvals
        </button></li>}
        {canWrite && <li className="nav-item"><button className={`nav-link ${activeTab==='myuploads'?'active':''}`} onClick={() => setActiveTab('myuploads')}>
          <i className="bi bi-person-check me-1"/>My Uploads
        </button></li>}
        {canWrite && <li className="nav-item"><button className={`nav-link ${activeTab==='doubts'?'active':''}`} onClick={() => setActiveTab('doubts')}>
          <i className="bi bi-chat-square-text me-1"/>Student Doubts
        </button></li>}
      </ul>

      {activeTab === 'content' && (
        !canWrite ? (
          <StudentCourseContentView courseId={courseId} />
        ) : (
        <div className="row g-3">
          <div className="col-md-4 col-lg-3">
            <div className="card">
              <div className="card-header py-2 fw-semibold small">Structure</div>
              <div className="card-body p-2" style={{ maxHeight: '65vh', overflowY:'auto' }}>
                <StructureManager
                  courseId={courseId}
                  canWrite={canWrite}
                  canAdmin={canAdmin}
                  selectedModuleId={selectedModId}
                  selectedTopicId={selectedTopId}
                  onModuleSelect={setSelectedModId}
                  onTopicSelect={(topId, modId) => { setSelectedTopId(topId); setSelectedModId(modId); }}
                  courses={courses}
                  onAlert={showAlert}
                />
              </div>
            </div>
          </div>
          <div className="col-md-8 col-lg-9">
            <div className="card">
              <div className="card-body" style={{ minHeight: '65vh' }}>
                <TopicContentPanel
                  topicId={selectedTopId}
                  canWrite={canWrite}
                  canAdmin={canAdmin}
                  onUploadClick={() => setShowUpload(true)}
                  refreshKey={topicRefreshKey}
                />
              </div>
            </div>
          </div>
        </div>
        )
      )}

      {activeTab === 'reusable' && canWrite && (
        <ReusableBankPanel courses={courses} canWrite={canWrite} canAdmin={canAdmin}/>
      )}

      {activeTab === 'pending' && canAdmin && (
        <div className="card"><div className="card-body"><PendingTab canAdmin={canAdmin} canWrite={canWrite}/></div></div>
      )}

      {activeTab === 'myuploads' && canWrite && (
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="fw-semibold">My Uploads ({myItems.length})</span>
              <button className="btn btn-outline-secondary btn-sm" onClick={fetchMyItems}><i className="bi bi-arrow-clockwise"/></button>
            </div>
            {myLoading ? (
              <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"/></div>
            ) : myItems.length === 0 ? (
              <div className="text-center py-5 text-muted"><i className="bi bi-upload fs-2 d-block mb-2"/>No uploads yet</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover table-sm">
                  <thead className="table-light"><tr>
                    <th>Title</th><th>Course</th><th>Module</th><th>Topic</th><th>Type</th><th>Status</th><th>Date</th>
                  </tr></thead>
                  <tbody>
                    {myItems.map(item => {
                      const [sc, sb] = statusBadge(item.status);
                      return (
                        <tr key={item._id}>
                          <td><i className={`bi ${typeIcon(item.type)} text-${typeColor(item.type)} me-1`}/>{item.title}</td>
                          <td className="small">{item.courseId?.name || '—'}</td>
                          <td className="small">{item.moduleId?.title || '—'}</td>
                          <td className="small">{item.topicId?.title || '—'}</td>
                          <td><span className={`badge bg-${typeColor(item.type)}`}>{item.type}</span></td>
                          <td><span className={`badge bg-${sc}`}>{sb}</span></td>
                          <td className="small">{new Date(item.createdAt).toLocaleDateString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'doubts' && canWrite && (
        <CourseDoubtsPanel courseId={courseId} onAlert={showAlert} />
      )}

      <UploadModal
        show={showUpload}
        onClose={() => setShowUpload(false)}
        courses={courses}
        courseId={courseId}
        setCourseId={setCourseId}
        onUploaded={() => { showAlert('Content uploaded successfully.'); setTopicRefreshKey(k => k + 1); }}
        initialModId={selectedModId || ''}
        initialTopId={selectedTopId || ''}
      />
    </div>
  );
}
